/**
 * QSignalGridCard - MT5-style Signal Card for Q AI Coach
 *
 * Compact indicator card displaying individual symbol signal data.
 * Used in the QSignalGrid (Phase 1 MT5-style polish).
 *
 * Data Flow:
 * - Receives signal data from QSignalTable/QSignalGrid parent
 * - Uses signalBoardTheme for consistent styling
 *
 * Features:
 * - Collapsed: Symbol + Signal + Score
 * - Expanded: Full levels (Pivot, S1/R1, Fib, EMAs)
 * - Click-to-expand interaction
 * - "Ask Q" action button
 *
 * Phase 1C Enhancements:
 * - EMA Trend Matrix with multi-TF bars-above (H4/D1)
 * - Full Fibonacci grid with swing high/low
 * - Complete ADX/ATR pivot table
 * - Strategy DNA level-by-level breakdown table
 *
 * Phase 2 Enhancements (Institutional Grade):
 * - User-friendly QUICK SUMMARY at top for non-technical users
 * - Enhanced Strategy DNA with bold outcome visualization
 * - Full 7-level Fibonacci horizontal grid
 * - Clear visual hierarchy with bold/prominent data
 * - Session and trend badges in header
 *
 * @version 3.0.0
 * @phase Phase 2 - Institutional Grade UI/UX
 */

import React, { memo, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Collapse, IconButton, Tooltip, LinearProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
// PHASE 3.2: Section header icons
import BiotechIcon from '@mui/icons-material/Biotech';          // DNA icon
import TimelineIcon from '@mui/icons-material/Timeline';        // EMA icon
import SpeedIcon from '@mui/icons-material/Speed';              // ADX/Momentum icon
import SsidChartIcon from '@mui/icons-material/SsidChart';      // Fibonacci icon
import ContentCopyIcon from '@mui/icons-material/ContentCopy';  // PHASE 7.1: Copy icon
import NotificationsIcon from '@mui/icons-material/Notifications'; // PHASE 7.1: Alert icon
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'; // PHASE 7.1: Trade icon
import {
    COLORS,
    TYPOGRAPHY,
    SPACING,
    RADIUS,
    getSignalColor,
    getSignalBg,
    getSignalBorder
} from './signalBoardTheme';

// PHASE 7.1: Trade Level Calculator imports
import {
    calculateTradeLevels,
    calculateRiskReward,
    calculatePipDistance as calcTradePipDistance,
    getTradeDirection,
    formatTradePrice
} from '../../services/tradeLevelCalculator';

// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 1C: Helper Functions for Enhanced Sections
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get trend color based on position and bars count
 */
const getTrendColor = (position, bars) => {
    if (!position) return '#6b7280';
    const isAbove = position?.toUpperCase() === 'ABOVE';
    const strength = bars > 10 ? 'strong' : bars > 5 ? 'moderate' : 'weak';

    if (isAbove) {
        return strength === 'strong' ? '#22c55e' : strength === 'moderate' ? '#4ade80' : '#86efac';
    } else {
        return strength === 'strong' ? '#ef4444' : strength === 'moderate' ? '#f87171' : '#fca5a5';
    }
};

/**
 * Get direction color for trend labels
 */
const getDirectionColor = (direction) => {
    if (!direction) return '#6b7280';
    const dir = direction.toUpperCase();
    if (dir.includes('STRONG') && dir.includes('UP')) return '#22c55e';
    if (dir.includes('UP')) return '#4ade80';
    if (dir.includes('STRONG') && dir.includes('DOWN')) return '#ef4444';
    if (dir.includes('DOWN')) return '#f87171';
    return '#fbbf24';
};

/**
 * Get ADX color based on value and range
 */
const getAdxColor = (value, range) => {
    if (!value && !range) return '#6b7280';
    const adx = parseFloat(value) || 0;
    const r = range?.toUpperCase() || '';

    if (adx >= 40 || r.includes('STRONG')) return '#22c55e';
    if (adx >= 25 || r.includes('GOOD')) return '#4ade80';
    if (adx >= 15 || r.includes('WEAK')) return '#fbbf24';
    return '#9ca3af';
};

/**
 * Get Fibonacci level color
 */
const getFibLevelColor = (status, isNearest) => {
    if (isNearest) return '#fbbf24';
    if (!status) return '#6b7280';
    const s = status.toUpperCase();
    if (s === 'ABOVE') return '#22c55e';
    if (s === 'BELOW') return '#ef4444';
    if (s === 'AT') return '#fbbf24';
    return '#9ca3af';
};

/**
 * Get bounce/break/fakeout rate color
 */
const getRateColor = (rate, type = 'bounce') => {
    if (rate == null) return '#6b7280';
    if (type === 'fakeout') {
        if (rate <= 15) return '#22c55e';
        if (rate <= 30) return '#fbbf24';
        return '#ef4444';
    }
    if (rate >= 60) return '#22c55e';
    if (rate >= 40) return '#fbbf24';
    return '#ef4444';
};

/**
 * Format price based on magnitude
 */
const formatEmaPrice = (value) => {
    if (!value && value !== 0) return '--';
    if (value > 1000) return value.toFixed(2);
    if (value > 10) return value.toFixed(4);
    return value.toFixed(5);
};

/**
 * Get signal verdict from signal data
 */
const getSignalVerdict = (signal) => {
    const { qSignal, qScore, zSignal, zScore, trendScore } = signal;
    const score = qScore ?? trendScore ?? 50;

    if (qSignal) {
        const upper = qSignal.toUpperCase();
        if (upper === 'HOLD' || upper === 'WAIT' || upper === 'NEUTRAL') {
            return { action: 'NEUTRAL', type: 'neutral', score };
        }
        if (upper.includes('BUY')) return { action: 'BULLISH', type: 'buy', score };
        if (upper.includes('SELL')) return { action: 'BEARISH', type: 'sell', score };
    }

    if (score <= 60) return { action: 'NEUTRAL', type: 'neutral', score };

    let direction = 'neutral';
    if (zSignal) {
        const upper = zSignal.toUpperCase();
        if (upper.includes('BUY')) direction = 'buy';
        else if (upper.includes('SELL')) direction = 'sell';
    } else if (zScore !== undefined) {
        direction = zScore < 0 ? 'buy' : zScore > 0 ? 'sell' : 'neutral';
    }

    if (direction === 'buy') return { action: 'BULLISH', type: 'buy', score };
    if (direction === 'sell') return { action: 'BEARISH', type: 'sell', score };
    return { action: 'NEUTRAL', type: 'neutral', score };
};

/**
 * Format price value
 */
const formatPrice = (value) => {
    if (value === null || value === undefined) return '--';
    const abs = Math.abs(value);
    if (abs >= 1000) return value.toFixed(1);
    if (abs >= 100) return value.toFixed(2);
    if (abs >= 1) return value.toFixed(3);
    return value.toFixed(4);
};

/**
 * Level Row Component
 */
const LevelRow = memo(({ label, value, color, bgColor }) => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '5px 8px',
            backgroundColor: bgColor || 'rgba(255, 255, 255, 0.02)',
            borderRadius: RADIUS.xs,
            borderLeft: color ? `2px solid ${color}` : 'none'
        }}
    >
        <Typography
            sx={{
                fontSize: '10px',
                fontWeight: 600,
                color: COLORS.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.03em'
            }}
        >
            {label}
        </Typography>
        <Typography
            sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: color || COLORS.textSecondary,
                fontFamily: "'JetBrains Mono', monospace"
            }}
        >
            {formatPrice(value)}
        </Typography>
    </Box>
));
LevelRow.displayName = 'LevelRow';

// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 2: USER-FRIENDLY QUICK SUMMARY COMPONENT
// Shows clear, bold outcome for non-technical users
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get user-friendly recommendation text based on signal and DNA
 */
const getRecommendationText = (verdict, dnaData, trendScore) => {
    const score = verdict?.score || trendScore || 50;
    const bounceRate = dnaData?.bounceRate || dnaData?.bouncePercent || 0;
    const fakeoutRate = dnaData?.fakeoutRate || dnaData?.fakeoutPercent || 0;

    // Determine confidence level
    let confidence = 'LOW';
    if (score >= 80 && bounceRate >= 60 && fakeoutRate < 25) confidence = 'HIGH';
    else if (score >= 65 && bounceRate >= 50) confidence = 'MEDIUM';

    // Generate recommendation
    if (verdict?.type === 'buy') {
        if (confidence === 'HIGH') return { text: 'STRONG BUY ZONE', subtext: 'High probability bounce area', confidence };
        if (confidence === 'MEDIUM') return { text: 'POTENTIAL BUY', subtext: 'Monitor for entry confirmation', confidence };
        return { text: 'WEAK BUY SIGNAL', subtext: 'Wait for better setup', confidence };
    }
    if (verdict?.type === 'sell') {
        if (confidence === 'HIGH') return { text: 'STRONG SELL ZONE', subtext: 'High probability reversal area', confidence };
        if (confidence === 'MEDIUM') return { text: 'POTENTIAL SELL', subtext: 'Monitor for entry confirmation', confidence };
        return { text: 'WEAK SELL SIGNAL', subtext: 'Wait for better setup', confidence };
    }

    if (fakeoutRate > 30) return { text: 'HIGH RISK ZONE', subtext: `${fakeoutRate.toFixed(0)}% fakeout rate - use caution`, confidence: 'WARNING' };
    return { text: 'WAIT FOR SIGNAL', subtext: 'No clear direction yet', confidence: 'NEUTRAL' };
};

/**
 * QuickSummaryPanel - User-friendly outcome display
 * Large, bold, easy to understand at a glance
 */
const QuickSummaryPanel = memo(({ verdict, dnaData, trendScore, session }) => {
    const recommendation = getRecommendationText(verdict, dnaData, trendScore);

    const getConfidenceColor = (conf) => {
        switch(conf) {
            case 'HIGH': return '#22c55e';
            case 'MEDIUM': return '#fbbf24';
            case 'WARNING': return '#ef4444';
            default: return '#6b7280';
        }
    };

    const getBgColor = (type) => {
        if (type === 'buy') return 'rgba(34, 197, 94, 0.08)';
        if (type === 'sell') return 'rgba(239, 68, 68, 0.08)';
        return 'rgba(107, 114, 128, 0.08)';
    };

    const getBorderColor = (type) => {
        if (type === 'buy') return 'rgba(34, 197, 94, 0.4)';
        if (type === 'sell') return 'rgba(239, 68, 68, 0.4)';
        return 'rgba(107, 114, 128, 0.4)';
    };

    const TrendIcon = verdict?.type === 'buy' ? TrendingUpIcon :
                      verdict?.type === 'sell' ? TrendingDownIcon : TrendingFlatIcon;

    // AUDIT: Optimized for institutional density
    return (
        <Box
            sx={{
                backgroundColor: getBgColor(verdict?.type),
                border: `1px solid ${getBorderColor(verdict?.type)}`,
                borderRadius: '4px',
                padding: '6px 10px',
                marginBottom: '4px'
            }}
        >
            {/* Main Recommendation - AUDIT: Compact layout */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <TrendIcon sx={{
                        fontSize: 18,
                        color: verdict?.type === 'buy' ? '#22c55e' :
                               verdict?.type === 'sell' ? '#ef4444' : '#6b7280'
                    }} />
                    <Box>
                        <Typography
                            sx={{
                                fontSize: '13px',
                                fontWeight: 700,
                                color: verdict?.type === 'buy' ? '#22c55e' :
                                       verdict?.type === 'sell' ? '#ef4444' : '#9ca3af',
                                letterSpacing: '0.02em',
                                lineHeight: 1.1
                            }}
                        >
                            {recommendation.text}
                        </Typography>
                        <Typography sx={{ fontSize: '9px', color: '#9ca3af', mt: 0.1 }}>
                            {recommendation.subtext}
                        </Typography>
                    </Box>
                </Box>

                {/* Confidence + Score - AUDIT: Reduced from 28px to 20px */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {session && (
                        <Typography sx={{ fontSize: '9px', color: '#fbbf24', fontWeight: 600, letterSpacing: '0.02em' }}>
                            {session}
                        </Typography>
                    )}
                    <Box
                        sx={{
                            backgroundColor: `${getConfidenceColor(recommendation.confidence)}20`,
                            color: getConfidenceColor(recommendation.confidence),
                            padding: '2px 8px',
                            borderRadius: '4px',
                            border: `1px solid ${getConfidenceColor(recommendation.confidence)}50`,
                            display: 'inline-flex',
                            alignItems: 'baseline'
                        }}
                    >
                        <Typography sx={{ fontSize: '20px', fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>
                            {verdict?.score || trendScore || '--'}
                        </Typography>
                        <Typography sx={{ fontSize: '10px', fontWeight: 600, color: getConfidenceColor(recommendation.confidence), opacity: 0.7 }}>
                            /100
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Quick Stats Row - AUDIT: Inline with main row when no DNA data */}
            {dnaData && (dnaData.bounceRate || dnaData.bouncePercent) && (
                <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <Typography sx={{ fontSize: '9px', color: '#6b7280' }}>B:</Typography>
                        <Typography sx={{
                            fontSize: '10px',
                            fontWeight: 700,
                            color: (dnaData.bounceRate || dnaData.bouncePercent) >= 60 ? '#22c55e' : '#fbbf24',
                            fontFamily: "'JetBrains Mono', monospace"
                        }}>
                            {(dnaData.bounceRate || dnaData.bouncePercent || 0).toFixed(0)}%
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <Typography sx={{ fontSize: '9px', color: '#6b7280' }}>F:</Typography>
                        <Typography sx={{
                            fontSize: '10px',
                            fontWeight: 700,
                            color: (dnaData.fakeoutRate || dnaData.fakeoutPercent) > 25 ? '#ef4444' : '#22c55e',
                            fontFamily: "'JetBrains Mono', monospace"
                        }}>
                            {(dnaData.fakeoutRate || dnaData.fakeoutPercent || 0).toFixed(0)}%
                        </Typography>
                    </Box>
                    {dnaData.totalEvents > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <Typography sx={{ fontSize: '9px', color: '#6b7280' }}>#:</Typography>
                            <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#9ca3af', fontFamily: "'JetBrains Mono', monospace" }}>
                                {dnaData.totalEvents}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
});
QuickSummaryPanel.displayName = 'QuickSummaryPanel';

// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 7.1: TRADE SETUP ROW COMPONENT
// Displays Entry, SL, TP1, TP2, R:R with action buttons
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * TradeSetupRow - Trade setup with entry, stop loss, take profits and R:R
 * PHASE 7.1: Full-width component positioned after QuickSummaryPanel
 */
const TradeSetupRow = memo(({
    direction,
    entry,
    stopLoss,
    takeProfit1,
    takeProfit2,
    confidence,
    reasons = {},
    onAskQ
}) => {
    if (!direction || !entry) return null;

    const isLong = direction === 'LONG';
    const directionColor = isLong ? '#22c55e' : '#ef4444';
    const directionBg = isLong ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.08)';
    const directionBorder = isLong ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)';

    // Calculate R:R for TP1
    const rr1 = calculateRiskReward(entry, stopLoss, takeProfit1);
    const rr2 = calculateRiskReward(entry, stopLoss, takeProfit2);

    // Copy handler
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).catch(err => console.log('Copy failed:', err));
    };

    const handleCopyAll = (e) => {
        e.stopPropagation();
        const text = `Entry: ${formatTradePrice(entry, entry)} | SL: ${formatTradePrice(stopLoss, entry)} (${reasons.sl || '--'}) | TP1: ${formatTradePrice(takeProfit1, entry)} (${reasons.tp1 || '--'}) | TP2: ${formatTradePrice(takeProfit2, entry)} (${reasons.tp2 || '--'}) | R:R: 1:${rr1.ratio || '--'}`;
        copyToClipboard(text);
    };

    const handleCopySLTP = (e) => {
        e.stopPropagation();
        const text = `SL: ${formatTradePrice(stopLoss, entry)} | TP: ${formatTradePrice(takeProfit1, entry)}`;
        copyToClipboard(text);
    };

    return (
        <Box
            sx={{
                backgroundColor: directionBg,
                borderRadius: '4px',
                border: `1px solid ${directionBorder}`,
                borderLeft: `3px solid ${directionColor}`,
                overflow: 'hidden',
                marginBottom: '4px'
            }}
        >
            {/* Header Row */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '4px 8px',
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    borderBottom: `1px solid ${directionBorder}`
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountBalanceWalletIcon sx={{ fontSize: '14px', color: directionColor }} />
                    <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        TRADE SETUP
                    </Typography>
                    <Box
                        sx={{
                            backgroundColor: `${directionColor}20`,
                            color: directionColor,
                            fontSize: '9px',
                            fontWeight: 700,
                            padding: '1px 6px',
                            borderRadius: '3px',
                            border: `1px solid ${directionColor}40`
                        }}
                    >
                        {isLong ? '↑ LONG' : '↓ SHORT'}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '10px', color: '#9ca3af' }}>Confidence:</Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: directionColor, fontFamily: "'JetBrains Mono', monospace" }}>
                        {confidence || '--'}%
                    </Typography>
                </Box>
            </Box>

            {/* Trade Levels Grid - 5 columns */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: '1px',
                    backgroundColor: 'rgba(55, 65, 81, 0.2)'
                }}
            >
                {/* Entry */}
                <Box sx={{ backgroundColor: '#0a0a0a', padding: '6px 4px', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '8px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', mb: 0.25 }}>ENTRY</Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#fff', fontFamily: "'JetBrains Mono', monospace" }}>
                        {formatTradePrice(entry, entry)}
                    </Typography>
                    <Typography sx={{ fontSize: '8px', color: '#6b7280' }}>Current</Typography>
                </Box>

                {/* Stop Loss */}
                <Box sx={{ backgroundColor: '#0a0a0a', padding: '6px 4px', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '8px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', mb: 0.25 }}>STOP LOSS</Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#ef4444', fontFamily: "'JetBrains Mono', monospace" }}>
                        {formatTradePrice(stopLoss, entry)}
                    </Typography>
                    <Typography sx={{ fontSize: '8px', color: '#9ca3af' }}>
                        {calcTradePipDistance(stopLoss, entry)} ({reasons.sl || '--'})
                    </Typography>
                </Box>

                {/* Take Profit 1 */}
                <Box sx={{ backgroundColor: '#0a0a0a', padding: '6px 4px', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '8px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', mb: 0.25 }}>TP 1</Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#22c55e', fontFamily: "'JetBrains Mono', monospace" }}>
                        {formatTradePrice(takeProfit1, entry)}
                    </Typography>
                    <Typography sx={{ fontSize: '8px', color: '#9ca3af' }}>
                        {calcTradePipDistance(takeProfit1, entry)} ({reasons.tp1 || '--'})
                    </Typography>
                </Box>

                {/* Take Profit 2 */}
                <Box sx={{ backgroundColor: '#0a0a0a', padding: '6px 4px', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '8px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', mb: 0.25 }}>TP 2</Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#22c55e', fontFamily: "'JetBrains Mono', monospace" }}>
                        {formatTradePrice(takeProfit2, entry)}
                    </Typography>
                    <Typography sx={{ fontSize: '8px', color: '#9ca3af' }}>
                        {calcTradePipDistance(takeProfit2, entry)} ({reasons.tp2 || '--'})
                    </Typography>
                </Box>

                {/* R:R Ratio */}
                <Box sx={{ backgroundColor: '#0a0a0a', padding: '6px 4px', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '8px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', mb: 0.25 }}>R:R</Typography>
                    <Typography sx={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: rr1.isGood ? '#22c55e' : rr1.isAcceptable ? '#fbbf24' : '#ef4444',
                        fontFamily: "'JetBrains Mono', monospace"
                    }}>
                        1:{rr1.ratio || '--'}
                    </Typography>
                    <Typography sx={{ fontSize: '8px', color: rr1.isGood ? '#22c55e' : rr1.isAcceptable ? '#fbbf24' : '#9ca3af' }}>
                        {rr1.isGood ? '✓ Good' : rr1.isAcceptable ? '⚠ OK' : '✗ Poor'}
                    </Typography>
                </Box>
            </Box>

            {/* Action Buttons Row */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '4px 8px',
                    backgroundColor: 'rgba(0, 0, 0, 0.15)',
                    borderTop: `1px solid ${directionBorder}`
                }}
            >
                {/* Copy All Button */}
                <Tooltip title="Copy all trade levels" arrow>
                    <Box
                        onClick={handleCopyAll}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            padding: '3px 8px',
                            backgroundColor: 'rgba(55, 65, 81, 0.3)',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            transition: 'all 0.12s ease',
                            '&:hover': { backgroundColor: 'rgba(55, 65, 81, 0.5)' }
                        }}
                    >
                        <ContentCopyIcon sx={{ fontSize: '10px', color: '#9ca3af' }} />
                        <Typography sx={{ fontSize: '9px', color: '#b4bcd0' }}>Copy All</Typography>
                    </Box>
                </Tooltip>

                {/* Copy SL/TP Button */}
                <Tooltip title="Copy SL and TP only" arrow>
                    <Box
                        onClick={handleCopySLTP}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            padding: '3px 8px',
                            backgroundColor: 'rgba(55, 65, 81, 0.3)',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            transition: 'all 0.12s ease',
                            '&:hover': { backgroundColor: 'rgba(55, 65, 81, 0.5)' }
                        }}
                    >
                        <ContentCopyIcon sx={{ fontSize: '10px', color: '#9ca3af' }} />
                        <Typography sx={{ fontSize: '9px', color: '#b4bcd0' }}>SL/TP</Typography>
                    </Box>
                </Tooltip>

                {/* Alert Button (Placeholder) */}
                <Tooltip title="Set price alert" arrow>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            padding: '3px 8px',
                            backgroundColor: 'rgba(55, 65, 81, 0.3)',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            transition: 'all 0.12s ease',
                            '&:hover': { backgroundColor: 'rgba(55, 65, 81, 0.5)' }
                        }}
                    >
                        <NotificationsIcon sx={{ fontSize: '10px', color: '#9ca3af' }} />
                        <Typography sx={{ fontSize: '9px', color: '#b4bcd0' }}>Alert</Typography>
                    </Box>
                </Tooltip>

                {/* Spacer */}
                <Box sx={{ flex: 1 }} />

                {/* Ask Q.AI Button */}
                {onAskQ && (
                    <Tooltip title="Ask Q.AI about this setup" arrow>
                        <Box
                            onClick={(e) => { e.stopPropagation(); onAskQ(); }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                padding: '3px 10px',
                                backgroundColor: 'rgba(0, 212, 255, 0.1)',
                                border: '1px solid rgba(0, 212, 255, 0.25)',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                transition: 'all 0.12s ease',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 212, 255, 0.2)',
                                    borderColor: 'rgba(0, 212, 255, 0.4)'
                                }
                            }}
                        >
                            <ShowChartIcon sx={{ fontSize: '11px', color: '#00d4ff' }} />
                            <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#00d4ff' }}>Ask Q.AI Why</Typography>
                        </Box>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
});
TradeSetupRow.displayName = 'TradeSetupRow';

// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 8.1: KEY LEVELS TABLE COMPONENT
// Shows all significant levels sorted by distance from current price
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * KeyLevelsTable - Consolidated key price levels sorted by distance
 * PHASE 8.1: Shows resistance/support levels with source and strength
 */
const KeyLevelsTable = memo(({ levels = [], currentPrice }) => {
    if (!levels.length || !currentPrice) return null;

    // Sort levels by distance from current price
    const sortedLevels = [...levels].sort((a, b) =>
        Math.abs(a.price - currentPrice) - Math.abs(b.price - currentPrice)
    );

    // Calculate pip distance
    const calcPips = (level) => {
        if (!level || !currentPrice) return '--';
        const pipValue = currentPrice > 100 ? 0.01 : 0.0001;
        const pips = Math.round((level - currentPrice) / pipValue);
        return pips > 0 ? `+${pips}p` : `${pips}p`;
    };

    // Get strength bar visualization
    const getStrengthBar = (strength) => {
        const filled = Math.min(Math.round((strength || 0) / 20), 5);
        return '█'.repeat(filled) + '░'.repeat(5 - filled);
    };

    // Get strength label
    const getStrengthLabel = (strength) => {
        if (strength >= 80) return 'V.Strong';
        if (strength >= 60) return 'Strong';
        if (strength >= 40) return 'Moderate';
        if (strength >= 20) return 'Weak';
        return 'Minimal';
    };

    return (
        <Box sx={{ backgroundColor: '#0a0a0a', borderRadius: '4px', overflow: 'hidden' }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '28px',
                    padding: '0 8px',
                    backgroundColor: '#1a1a24',
                    borderBottom: '1px solid rgba(55, 65, 81, 0.3)'
                }}
            >
                <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    KEY LEVELS
                </Typography>
                <Typography sx={{ fontSize: '9px', color: '#6b7280' }}>
                    Sorted by distance
                </Typography>
            </Box>

            {/* Table Header */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '60px 1fr 50px 55px 60px',
                    height: '24px',
                    alignItems: 'center',
                    backgroundColor: 'rgba(55, 65, 81, 0.15)',
                    borderBottom: '1px solid rgba(55, 65, 81, 0.2)',
                    padding: '0 6px'
                }}
            >
                <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>TYPE</Typography>
                <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', textAlign: 'right' }}>LEVEL</Typography>
                <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', textAlign: 'right' }}>DIST</Typography>
                <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', textAlign: 'center' }}>SOURCE</Typography>
                <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', textAlign: 'center' }}>STRENGTH</Typography>
            </Box>

            {/* Levels */}
            <Box sx={{ maxHeight: '140px', overflowY: 'auto' }}>
                {sortedLevels.slice(0, 8).map((level, index) => {
                    const isResistance = level.price > currentPrice;
                    const isCurrent = level.type === 'CURRENT';
                    const typeColor = isCurrent ? '#00d4ff' : isResistance ? '#ef4444' : '#22c55e';
                    const distanceColor = isCurrent ? '#6b7280' : isResistance ? '#ef4444' : '#22c55e';

                    return (
                        <Box
                            key={`${level.source}-${level.price}-${index}`}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '60px 1fr 50px 55px 60px',
                                height: '26px',
                                alignItems: 'center',
                                padding: '0 6px',
                                backgroundColor: isCurrent ? 'rgba(0, 212, 255, 0.08)' : 'transparent',
                                borderBottom: '1px solid rgba(55, 65, 81, 0.15)',
                                borderLeft: isCurrent ? '2px solid #00d4ff' : '2px solid transparent',
                                '&:hover': { backgroundColor: 'rgba(55, 65, 81, 0.1)' }
                            }}
                        >
                            <Typography sx={{ fontSize: '10px', fontWeight: 600, color: typeColor, fontFamily: "'JetBrains Mono', monospace" }}>
                                {isCurrent ? '▶ CURRENT' : (isResistance ? 'RESIST' : 'SUPPORT')}
                            </Typography>
                            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: isCurrent ? '#fff' : '#b4bcd0', fontFamily: "'JetBrains Mono', monospace", textAlign: 'right' }}>
                                {level.price?.toFixed(currentPrice > 100 ? 2 : 5)}
                            </Typography>
                            <Typography sx={{ fontSize: '10px', color: distanceColor, fontFamily: "'JetBrains Mono', monospace", textAlign: 'right' }}>
                                {isCurrent ? '--' : calcPips(level.price)}
                            </Typography>
                            <Typography sx={{ fontSize: '9px', color: '#9ca3af', textAlign: 'center' }}>
                                {level.source || '--'}
                            </Typography>
                            <Typography sx={{ fontSize: '9px', color: '#6b7280', fontFamily: "'JetBrains Mono', monospace", textAlign: 'center' }}>
                                {level.strength ? getStrengthBar(level.strength) : '░░░░░'}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
});
KeyLevelsTable.displayName = 'KeyLevelsTable';

// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 8.2: MARKET STRUCTURE PANEL COMPONENT
// Shows BOS/CHoCH events and Order Blocks
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * StructurePanel - Market structure events (BOS, CHoCH, Order Blocks)
 * PHASE 8.2: Displays structure events with status and score
 */
const StructurePanel = memo(({ events = [], score = 0 }) => {
    // Get event icon
    const getEventIcon = (type) => {
        if (!type) return '•';
        const t = type.toUpperCase();
        if (t.includes('BOS') && t.includes('BULL')) return '↗';
        if (t.includes('BOS') && t.includes('BEAR')) return '↘';
        if (t.includes('BOS')) return '→';
        if (t.includes('CHOCH')) return '↩';
        if (t.includes('ORDER') || t.includes('BLOCK')) return '▣';
        return '•';
    };

    // Get event color
    const getEventColor = (bias) => {
        if (!bias) return '#9ca3af';
        const b = bias.toUpperCase();
        if (b.includes('BULL') || b.includes('UP')) return '#22c55e';
        if (b.includes('BEAR') || b.includes('DOWN')) return '#ef4444';
        return '#9ca3af';
    };

    // Get status badge style
    const getStatusStyle = (status) => {
        if (!status) return { bg: 'rgba(107, 114, 128, 0.2)', color: '#9ca3af' };
        const s = status.toUpperCase();
        if (s.includes('CONFIRM')) return { bg: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' };
        if (s.includes('UNTEST')) return { bg: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24' };
        if (s.includes('VALID')) return { bg: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' };
        if (s.includes('TESTED')) return { bg: 'rgba(107, 114, 128, 0.3)', color: '#b4bcd0' };
        return { bg: 'rgba(107, 114, 128, 0.2)', color: '#9ca3af' };
    };

    return (
        <Box sx={{ backgroundColor: '#0a0a0a', borderRadius: '4px', overflow: 'hidden' }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '28px',
                    padding: '0 8px',
                    backgroundColor: '#1a1a24',
                    borderBottom: '1px solid rgba(55, 65, 81, 0.3)'
                }}
            >
                <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    MARKET STRUCTURE
                </Typography>
                <Box
                    sx={{
                        backgroundColor: 'rgba(55, 65, 81, 0.3)',
                        padding: '2px 6px',
                        borderRadius: '3px'
                    }}
                >
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#b4bcd0', fontFamily: "'JetBrains Mono', monospace" }}>
                        {score}/100
                    </Typography>
                </Box>
            </Box>

            {/* Content */}
            {events.length === 0 ? (
                <Box sx={{ padding: '12px 8px', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>
                        No structure events detected
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ maxHeight: '120px', overflowY: 'auto' }}>
                    {/* Table Header */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '55px 30px 1fr 60px 55px',
                            height: '22px',
                            alignItems: 'center',
                            backgroundColor: 'rgba(55, 65, 81, 0.15)',
                            borderBottom: '1px solid rgba(55, 65, 81, 0.2)',
                            padding: '0 6px'
                        }}
                    >
                        <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>EVENT</Typography>
                        <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', textAlign: 'center' }}>TF</Typography>
                        <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', textAlign: 'right' }}>LEVEL</Typography>
                        <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', textAlign: 'center' }}>TIME</Typography>
                        <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', textAlign: 'center' }}>STATUS</Typography>
                    </Box>

                    {/* Event Rows */}
                    {events.slice(0, 6).map((event, index) => {
                        const statusStyle = getStatusStyle(event.status);
                        return (
                            <Box
                                key={`${event.type}-${event.level}-${index}`}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '55px 30px 1fr 60px 55px',
                                    height: '24px',
                                    alignItems: 'center',
                                    padding: '0 6px',
                                    borderBottom: '1px solid rgba(55, 65, 81, 0.1)',
                                    '&:hover': { backgroundColor: 'rgba(55, 65, 81, 0.1)' }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography sx={{ fontSize: '10px', color: getEventColor(event.bias) }}>
                                        {getEventIcon(event.type)}
                                    </Typography>
                                    <Typography sx={{ fontSize: '9px', fontWeight: 600, color: getEventColor(event.bias) }}>
                                        {event.label || event.type || '--'}
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontSize: '9px', color: '#9ca3af', textAlign: 'center' }}>
                                    {event.timeframe || '--'}
                                </Typography>
                                <Typography sx={{ fontSize: '10px', color: '#b4bcd0', fontFamily: "'JetBrains Mono', monospace", textAlign: 'right' }}>
                                    {event.level?.toFixed ? event.level.toFixed(2) : (event.level || '--')}
                                </Typography>
                                <Typography sx={{ fontSize: '9px', color: '#6b7280', textAlign: 'center' }}>
                                    {event.time || '--'}
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Box
                                        sx={{
                                            backgroundColor: statusStyle.bg,
                                            color: statusStyle.color,
                                            fontSize: '8px',
                                            fontWeight: 600,
                                            padding: '1px 4px',
                                            borderRadius: '2px',
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {event.status || 'N/A'}
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
});
StructurePanel.displayName = 'StructurePanel';

// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 3.2: STANDARDIZED SECTION HEADER COMPONENT
// 36px height, 11px UPPERCASE, 16x16 icon, right-aligned badge
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * SectionHeader - Standardized header for all DNA panels
 * PHASE 3.2: Compact 36px height with consistent styling
 * PHASE 3.3: Added compact prop (28px) for dense components like Fibonacci
 */
const SectionHeader = memo(({ icon: IconComponent, title, badge, badgeColor = 'cyan', subtitle, children, compact = false }) => {
    // Color mapping for badges
    const colorMap = {
        cyan: { bg: 'rgba(0, 212, 255, 0.12)', border: 'rgba(0, 212, 255, 0.25)', text: '#00d4ff' },
        green: { bg: 'rgba(34, 197, 94, 0.12)', border: 'rgba(34, 197, 94, 0.25)', text: '#22c55e' },
        red: { bg: 'rgba(239, 68, 68, 0.12)', border: 'rgba(239, 68, 68, 0.25)', text: '#ef4444' },
        yellow: { bg: 'rgba(251, 191, 36, 0.12)', border: 'rgba(251, 191, 36, 0.25)', text: '#fbbf24' },
        blue: { bg: 'rgba(59, 130, 246, 0.12)', border: 'rgba(59, 130, 246, 0.25)', text: '#3b82f6' },
        purple: { bg: 'rgba(168, 85, 247, 0.12)', border: 'rgba(168, 85, 247, 0.25)', text: '#a855f7' },
    };
    const badgeColors = colorMap[badgeColor] || colorMap.cyan;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: compact ? '28px' : '36px',  // PHASE 3.3: 28px compact mode, 36px normal
                padding: compact ? '0 8px' : '0 12px',
                backgroundColor: '#1a1a24',  // var(--bg-surface-2)
                borderBottom: `1px solid ${COLORS.borderDefault}`,
                borderTopLeftRadius: '4px',
                borderTopRightRadius: '4px'
            }}
        >
            {/* Left: Icon + Title + Subtitle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? '6px' : '8px' }}>
                {/* Icon - 16x16 (14x14 in compact) */}
                {IconComponent && (
                    <IconComponent sx={{ fontSize: compact ? '14px' : '16px', width: compact ? 14 : 16, height: compact ? 14 : 16, color: '#00d4ff' }} />
                )}
                {/* Title - 11px UPPERCASE (10px in compact) */}
                <Typography
                    sx={{
                        fontSize: compact ? '10px' : '11px',
                        fontWeight: 600,
                        color: COLORS.textSecondary,
                        fontFamily: "'Inter', sans-serif",
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                    }}
                >
                    {title}
                </Typography>
                {/* Subtitle (optional) */}
                {subtitle && (
                    <Typography sx={{ fontSize: compact ? '9px' : '10px', color: COLORS.textMuted }}>
                        {subtitle}
                    </Typography>
                )}
            </Box>

            {/* Right: Badge or custom children */}
            {badge && (
                <Box
                    sx={{
                        backgroundColor: badgeColors.bg,
                        border: `1px solid ${badgeColors.border}`,
                        padding: compact ? '1px 6px' : '2px 8px',
                        borderRadius: '4px'
                    }}
                >
                    <Typography sx={{ fontSize: compact ? '9px' : '10px', fontWeight: 700, color: badgeColors.text, fontFamily: "'JetBrains Mono', monospace" }}>
                        {badge}
                    </Typography>
                </Box>
            )}
            {children}
        </Box>
    );
});
SectionHeader.displayName = 'SectionHeader';

// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 9: COMPACT ASK Q BUTTON COMPONENT
// Reusable Ask Q button for indicator section headers
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * CompactAskQButton - Minimal Ask Q button for section headers
 * Used inside SectionHeader children slot for consistent styling
 * @param {Function} onAskQ - Callback to trigger Q.AI query
 * @param {string} tooltip - Custom tooltip text
 */
const CompactAskQButton = memo(({ onAskQ, tooltip = "Ask Q.AI about this" }) => {
    if (!onAskQ) return null;

    return (
        <Tooltip title={tooltip} arrow>
            <Box
                onClick={(e) => { e.stopPropagation(); onAskQ(); }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    padding: '2px 6px',
                    backgroundColor: 'rgba(0, 212, 255, 0.08)',
                    border: '1px solid rgba(0, 212, 255, 0.2)',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    transition: 'all 0.12s ease',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 212, 255, 0.15)',
                        borderColor: 'rgba(0, 212, 255, 0.35)'
                    }
                }}
            >
                <ShowChartIcon sx={{ fontSize: '10px', color: '#00d4ff' }} />
                <Typography sx={{ fontSize: '8px', fontWeight: 600, color: '#00d4ff', letterSpacing: '0.02em' }}>Ask Q</Typography>
            </Box>
        </Tooltip>
    );
});
CompactAskQButton.displayName = 'CompactAskQButton';

// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 2: ENHANCED STRATEGY DNA SECTION
// Bold outcomes, clear visualization, user-friendly
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * EnhancedDNASection - Institutional grade Strategy DNA display
 * PHASE 3.2: Uses standardized SectionHeader
 * G1: Enhanced with sortable columns, filter chips, avg hold duration
 * PHASE 9: Added onAskQ prop for Q.AI integration
 */
const EnhancedDNASection = memo(({ strategyDNA, strategyDNAStats = [], onAskQ }) => {
    // G1.1: State for sorting
    const [sortField, setSortField] = useState('bounceRate');
    const [sortDir, setSortDir] = useState('desc');
    // G1.2: State for filter chips
    const [activeFilter, setActiveFilter] = useState(null);

    // G1.2: Get unique level types for filter chips (MUST be before early return)
    const levelTypes = useMemo(() => {
        const types = new Set(strategyDNAStats.map(s => s.levelType).filter(Boolean));
        return Array.from(types);
    }, [strategyDNAStats]);

    // G1.1 + G1.2: Filter and sort stats (MUST be before early return)
    const processedStats = useMemo(() => {
        let filtered = [...strategyDNAStats];

        // Apply filter
        if (activeFilter) {
            filtered = filtered.filter(s => s.levelType === activeFilter);
        }

        // Apply sort
        filtered.sort((a, b) => {
            const aVal = a[sortField] ?? -Infinity;
            const bVal = b[sortField] ?? -Infinity;
            return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
        });

        return filtered.slice(0, 8); // Show up to 8 rows for density
    }, [strategyDNAStats, activeFilter, sortField, sortDir]);

    // Early return AFTER all hooks
    if (!strategyDNA) return null;

    const bounceRate = strategyDNA.bounceRate || strategyDNA.bouncePercent || strategyDNA.bounce || 0;
    const breakRate = strategyDNA.breakRate || strategyDNA.breakPercent || strategyDNA.break || 0;
    const fakeoutRate = strategyDNA.fakeoutRate || strategyDNA.fakeoutPercent || strategyDNA.fakeout || 0;
    const avgMfe = strategyDNA.avgMfePips || strategyDNA.avgMfe || 0;
    const avgMae = strategyDNA.avgMaePips || strategyDNA.avgMae || 0;
    const totalEvents = strategyDNA.totalEvents || 0;
    // G1.3: Extract avg hold duration (in minutes)
    const avgHoldMins = strategyDNA.time_to_outcome_mins || strategyDNA.avgHoldMins || strategyDNA.outcomeDetails?.time_to_outcome_mins || null;

    // Format hold duration for display
    const formatHoldDuration = (mins) => {
        if (!mins) return '--';
        if (mins < 60) return `${mins}m`;
        const hours = Math.floor(mins / 60);
        const remainMins = mins % 60;
        return remainMins > 0 ? `${hours}h${remainMins}m` : `${hours}h`;
    };

    // Determine dominant outcome
    const getDominantOutcome = () => {
        if (bounceRate >= 60) return { label: 'BOUNCE DOMINANT', color: '#22c55e', desc: 'Price tends to respect this level' };
        if (breakRate >= 50) return { label: 'BREAK DOMINANT', color: '#3b82f6', desc: 'Price tends to break through' };
        if (fakeoutRate >= 30) return { label: 'HIGH FAKEOUT', color: '#ef4444', desc: 'Beware of false breakouts' };
        return { label: 'MIXED SIGNALS', color: '#fbbf24', desc: 'No clear pattern' };
    };

    const dominant = getDominantOutcome();

    // Determine badge color based on dominant outcome
    const getBadgeColor = () => {
        if (bounceRate >= 60) return 'green';
        if (breakRate >= 50) return 'blue';
        if (fakeoutRate >= 30) return 'red';
        return 'yellow';
    };

    // G1.1: Handle column sort
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('desc');
        }
    };

    // G1.1: Sortable header cell renderer
    const SortableHeader = ({ field, label, color = '#9ca3af', width }) => {
        const isSorted = sortField === field;
        return (
            <Box
                onClick={() => handleSort(field)}
                sx={{
                    flex: `0 0 ${width}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.25,
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 }
                }}
            >
                <Typography sx={{
                    fontSize: '8px',
                    fontWeight: 700,
                    color: isSorted ? '#fbbf24' : color,
                    fontFamily: 'monospace',
                    textTransform: 'uppercase'
                }}>
                    {label}
                </Typography>
                {isSorted && (
                    <Typography sx={{ fontSize: '8px', color: '#fbbf24' }}>
                        {sortDir === 'desc' ? '▼' : '▲'}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Box sx={{ backgroundColor: '#0a0a0a', borderRadius: '4px', overflow: 'hidden' }}>
            {/* PHASE 3.2: Standardized Section Header + PHASE 9: Ask Q Button */}
            <SectionHeader
                icon={BiotechIcon}
                title="Strategy DNA"
                subtitle={`${totalEvents} events`}
                badge={dominant.label}
                badgeColor={getBadgeColor()}
            >
                <CompactAskQButton onAskQ={onAskQ} tooltip="Ask Q.AI about bounce/break patterns" />
            </SectionHeader>

            {/* Main Stats Row - AUDIT: Reduced from 22px to 16px, tighter padding */}
            <Box sx={{ padding: '8px 12px', borderBottom: '1px solid rgba(55, 65, 81, 0.3)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                    {/* Bounce */}
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '9px', color: '#9ca3af', mb: 0.25, fontWeight: 600, letterSpacing: '0.05em' }}>BOUNCE</Typography>
                        <Typography sx={{
                            fontSize: '16px',
                            fontWeight: 800,
                            color: bounceRate >= 60 ? '#22c55e' : bounceRate >= 40 ? '#fbbf24' : '#ef4444',
                            fontFamily: "'JetBrains Mono', monospace",
                            lineHeight: 1
                        }}>
                            {bounceRate.toFixed(1)}%
                        </Typography>
                        <Box sx={{ mt: 0.25, height: '3px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '2px' }}>
                            <Box sx={{ width: `${Math.min(bounceRate, 100)}%`, height: '100%', backgroundColor: '#22c55e', borderRadius: '2px' }} />
                        </Box>
                    </Box>

                    {/* Break */}
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '9px', color: '#9ca3af', mb: 0.25, fontWeight: 600, letterSpacing: '0.05em' }}>BREAK</Typography>
                        <Typography sx={{
                            fontSize: '16px',
                            fontWeight: 800,
                            color: '#3b82f6',
                            fontFamily: "'JetBrains Mono', monospace",
                            lineHeight: 1
                        }}>
                            {breakRate.toFixed(1)}%
                        </Typography>
                        <Box sx={{ mt: 0.25, height: '3px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '2px' }}>
                            <Box sx={{ width: `${Math.min(breakRate, 100)}%`, height: '100%', backgroundColor: '#3b82f6', borderRadius: '2px' }} />
                        </Box>
                    </Box>

                    {/* Fakeout */}
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '9px', color: '#9ca3af', mb: 0.25, fontWeight: 600, letterSpacing: '0.05em' }}>FAKEOUT</Typography>
                        <Typography sx={{
                            fontSize: '16px',
                            fontWeight: 800,
                            color: fakeoutRate > 25 ? '#ef4444' : fakeoutRate > 15 ? '#fbbf24' : '#22c55e',
                            fontFamily: "'JetBrains Mono', monospace",
                            lineHeight: 1
                        }}>
                            {fakeoutRate.toFixed(1)}%
                        </Typography>
                        <Box sx={{ mt: 0.25, height: '3px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '2px' }}>
                            <Box sx={{ width: `${Math.min(fakeoutRate, 100)}%`, height: '100%', backgroundColor: fakeoutRate > 25 ? '#ef4444' : '#22c55e', borderRadius: '2px' }} />
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* G1.3: MFE/MAE/Hold Duration Row - Enhanced with Avg Hold */}
            <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', padding: '4px 12px', borderBottom: '1px solid rgba(55, 65, 81, 0.3)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '9px', color: '#9ca3af', letterSpacing: '0.02em' }}>MFE:</Typography>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#22c55e', fontFamily: "'JetBrains Mono', monospace" }}>
                        +{avgMfe.toFixed(1)}p
                    </Typography>
                </Box>
                <Box sx={{ width: '1px', height: '12px', backgroundColor: 'rgba(55, 65, 81, 0.5)' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '9px', color: '#9ca3af', letterSpacing: '0.02em' }}>MAE:</Typography>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#ef4444', fontFamily: "'JetBrains Mono', monospace" }}>
                        {avgMae.toFixed(1)}p
                    </Typography>
                </Box>
                <Box sx={{ width: '1px', height: '12px', backgroundColor: 'rgba(55, 65, 81, 0.5)' }} />
                <Tooltip title="Average time to outcome resolution">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '9px', color: '#9ca3af', letterSpacing: '0.02em' }}>HOLD:</Typography>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#06b6d4', fontFamily: "'JetBrains Mono', monospace" }}>
                            {formatHoldDuration(avgHoldMins)}
                        </Typography>
                    </Box>
                </Tooltip>
            </Box>

            {/* G1.2: Filter Chips Row */}
            {levelTypes.length > 1 && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    padding: '3px 8px',
                    borderBottom: '1px solid rgba(55, 65, 81, 0.2)',
                    backgroundColor: 'rgba(55, 65, 81, 0.05)',
                    overflowX: 'auto'
                }}>
                    <Typography sx={{ fontSize: '8px', color: '#6b7280', fontFamily: 'monospace', mr: 0.5 }}>FILTER:</Typography>
                    <Box
                        onClick={() => setActiveFilter(null)}
                        sx={{
                            padding: '2px 6px',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            backgroundColor: !activeFilter ? 'rgba(220, 38, 38, 0.2)' : 'rgba(55, 65, 81, 0.3)',
                            border: !activeFilter ? '1px solid #dc2626' : '1px solid transparent',
                            '&:hover': { backgroundColor: 'rgba(220, 38, 38, 0.15)' }
                        }}
                    >
                        <Typography sx={{ fontSize: '8px', fontWeight: 700, color: !activeFilter ? '#dc2626' : '#9ca3af', fontFamily: 'monospace' }}>
                            ALL
                        </Typography>
                    </Box>
                    {levelTypes.map(type => (
                        <Box
                            key={type}
                            onClick={() => setActiveFilter(activeFilter === type ? null : type)}
                            sx={{
                                padding: '2px 6px',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                backgroundColor: activeFilter === type ? 'rgba(220, 38, 38, 0.2)' : 'rgba(55, 65, 81, 0.3)',
                                border: activeFilter === type ? '1px solid #dc2626' : '1px solid transparent',
                                '&:hover': { backgroundColor: 'rgba(220, 38, 38, 0.15)' }
                            }}
                        >
                            <Typography sx={{ fontSize: '8px', fontWeight: 700, color: activeFilter === type ? '#dc2626' : '#9ca3af', fontFamily: 'monospace' }}>
                                {type}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Level Stats Table */}
            {processedStats.length > 0 && (
                <Box sx={{ maxHeight: '160px', overflowY: 'auto' }}>
                    {/* G1.1: Table Header - Sortable columns */}
                    <Box
                        sx={{
                            display: 'flex',
                            padding: '3px 8px',
                            backgroundColor: 'rgba(55, 65, 81, 0.2)',
                            borderBottom: '1px solid rgba(55, 65, 81, 0.3)',
                            position: 'sticky',
                            top: 0,
                            zIndex: 1
                        }}
                    >
                        <Typography sx={{ flex: '0 0 60px', fontSize: '8px', fontWeight: 700, color: '#9ca3af', fontFamily: 'monospace' }}>LEVEL</Typography>
                        <Typography sx={{ flex: '0 0 35px', fontSize: '8px', fontWeight: 700, color: '#9ca3af', fontFamily: 'monospace', textAlign: 'center' }}>COND</Typography>
                        <SortableHeader field="totalEvents" label="#" width="25px" />
                        <SortableHeader field="bounceRate" label="BNCE" color="#22c55e" width="38px" />
                        <SortableHeader field="breakRate" label="BRK" color="#3b82f6" width="35px" />
                        <SortableHeader field="fakeoutRate" label="FAKE" color="#ef4444" width="38px" />
                        <SortableHeader field="avgMfePips" label="MFE" color="#22c55e" width="38px" />
                        <SortableHeader field="avgMaePips" label="MAE" color="#ef4444" width="38px" />
                    </Box>

                    {/* Table Rows */}
                    {processedStats.map((stat, index) => {
                        const levelLabel = `${stat.levelType || ''} ${stat.levelValue || ''}`.trim();
                        const cond = stat.trendDirection?.replace('STRONG_UPTREND', 'S.UP')
                            .replace('MODERATE_UPTREND', 'M.UP')
                            .replace('STRONG_DOWNTREND', 'S.DN')
                            .replace('MODERATE_DOWNTREND', 'M.DN')
                            .replace('NEUTRAL', 'NEUT') || '--';
                        const isHighFakeout = (stat.fakeoutRate || 0) > 30;
                        const p75Depth = stat.p75FakeoutDepth || stat.fakeoutDepthP75;
                        const p95Depth = stat.p95FakeoutDepth || stat.fakeoutDepthP95;
                        const hasDepthData = p75Depth || p95Depth;

                        return (
                            <Box
                                key={`${stat.levelType}-${stat.levelValue}-${index}`}
                                sx={{
                                    display: 'flex',
                                    padding: '3px 8px',
                                    borderBottom: '1px solid rgba(55, 65, 81, 0.12)',
                                    backgroundColor: isHighFakeout ? 'rgba(239, 68, 68, 0.06)' : 'transparent',
                                    borderLeft: isHighFakeout ? '2px solid #ef4444' : '2px solid transparent',
                                    '&:hover': { backgroundColor: 'rgba(55, 65, 81, 0.12)' }
                                }}
                            >
                                <Typography sx={{ flex: '0 0 60px', fontSize: '9px', fontWeight: 700, color: '#fff', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {levelLabel || '--'}
                                </Typography>
                                <Typography sx={{ flex: '0 0 35px', fontSize: '9px', color: '#9ca3af', fontFamily: 'monospace', textAlign: 'center' }}>
                                    {cond}
                                </Typography>
                                <Typography sx={{ flex: '0 0 25px', fontSize: '9px', color: '#6b7280', fontFamily: 'monospace', textAlign: 'center' }}>
                                    {stat.totalEvents || 0}
                                </Typography>
                                <Typography sx={{ flex: '0 0 38px', fontSize: '9px', fontWeight: 700, color: getRateColor(stat.bounceRate, 'bounce'), fontFamily: 'monospace', textAlign: 'center' }}>
                                    {stat.bounceRate?.toFixed(0) ?? '--'}%
                                </Typography>
                                <Typography sx={{ flex: '0 0 35px', fontSize: '9px', color: '#9ca3af', fontFamily: 'monospace', textAlign: 'center' }}>
                                    {stat.breakRate?.toFixed(0) ?? '--'}%
                                </Typography>
                                {/* Fakeout with Depth Tooltip */}
                                <Tooltip
                                    title={isHighFakeout && hasDepthData ? `Typical: ${p75Depth?.toFixed(1) || '--'}p | Extreme: ${p95Depth?.toFixed(1) || '--'}p` : ''}
                                    arrow
                                    placement="top"
                                >
                                    <Box sx={{ flex: '0 0 38px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, cursor: isHighFakeout ? 'help' : 'default' }}>
                                        <Typography sx={{ fontSize: '9px', fontWeight: isHighFakeout ? 700 : 400, color: getRateColor(stat.fakeoutRate, 'fakeout'), fontFamily: 'monospace' }}>
                                            {stat.fakeoutRate?.toFixed(0) ?? '--'}%
                                        </Typography>
                                        {isHighFakeout && <WarningAmberIcon sx={{ fontSize: 9, color: '#ef4444' }} />}
                                    </Box>
                                </Tooltip>
                                {/* MFE Column */}
                                <Typography sx={{ flex: '0 0 38px', fontSize: '9px', fontWeight: 600, color: '#22c55e', fontFamily: 'monospace', textAlign: 'center' }}>
                                    {stat.avgMfePips != null ? `+${stat.avgMfePips.toFixed(1)}` : '--'}
                                </Typography>
                                {/* MAE Column */}
                                <Typography sx={{ flex: '0 0 38px', fontSize: '9px', fontWeight: 600, color: '#ef4444', fontFamily: 'monospace', textAlign: 'center' }}>
                                    {stat.avgMaePips != null ? stat.avgMaePips.toFixed(1) : '--'}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/* Footer - Shows filter status and description */}
            <Box sx={{ padding: '3px 8px', backgroundColor: 'rgba(55, 65, 81, 0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '8px', color: '#6b7280', fontStyle: 'italic' }}>
                    {dominant.desc}
                </Typography>
                {activeFilter && (
                    <Typography sx={{ fontSize: '8px', color: '#dc2626', fontFamily: 'monospace' }}>
                        Filtered: {activeFilter}
                    </Typography>
                )}
            </Box>
        </Box>
    );
});
EnhancedDNASection.displayName = 'EnhancedDNASection';

// ═══════════════════════════════════════════════════════════════════════════════
// TASK 1.3: FAKEOUT WARNINGS PANEL
// Collapsible panel showing high-risk levels with fakeout depth
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * FakeoutWarningsPanel - Collapsible warning panel for high-risk levels
 * TASK 1.3: Displays levels with >30% fakeout rate and depth data
 * G2: Added pulsing glow animation for active warnings
 */
const FakeoutWarningsPanel = memo(({ warnings = [], defaultExpanded = true }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    if (!warnings.length) return null;

    return (
        <Box
            sx={{
                backgroundColor: 'rgba(239, 68, 68, 0.08)',
                borderRadius: '4px',
                border: '1px solid rgba(239, 68, 68, 0.25)',
                overflow: 'hidden',
                marginTop: '4px',
                // G2: Pulsing glow animation
                '@keyframes pulseGlow': {
                    '0%': { boxShadow: '0 0 2px rgba(239, 68, 68, 0.3)' },
                    '50%': { boxShadow: '0 0 8px rgba(239, 68, 68, 0.6)' },
                    '100%': { boxShadow: '0 0 2px rgba(239, 68, 68, 0.3)' }
                },
                animation: 'pulseGlow 2s ease-in-out infinite'
            }}
        >
            {/* Header - Clickable to expand/collapse */}
            <Box
                onClick={() => setExpanded(!expanded)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '4px 8px',
                    backgroundColor: 'rgba(239, 68, 68, 0.15)',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.2)' }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {/* G2: Animated warning icon */}
                    <WarningAmberIcon sx={{
                        fontSize: 12,
                        color: '#ef4444',
                        '@keyframes iconPulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.6 }
                        },
                        animation: 'iconPulse 1.5s ease-in-out infinite'
                    }} />
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        FAKEOUT WARNINGS ({warnings.length})
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: '10px', color: '#9ca3af' }}>
                    {expanded ? '▲' : '▼'}
                </Typography>
            </Box>

            {/* Content - Collapsible */}
            {expanded && (
                <Box sx={{ padding: '4px' }}>
                    {warnings.slice(0, 3).map((warning, index) => {
                        const levelLabel = `${warning.levelType || ''} ${warning.levelValue || ''}`.trim();
                        const condLabel = warning.trendDirection?.replace('STRONG_UPTREND', 'S.UP')
                            .replace('MODERATE_UPTREND', 'M.UP')
                            .replace('STRONG_DOWNTREND', 'S.DN')
                            .replace('MODERATE_DOWNTREND', 'M.DN')
                            .replace('NEUTRAL', 'NEUT') || '';
                        const p75 = warning.p75FakeoutDepth || warning.fakeoutDepthP75;
                        const p95 = warning.p95FakeoutDepth || warning.fakeoutDepthP95;

                        return (
                            <Box
                                key={`warning-${index}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '4px 6px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                    borderRadius: '3px',
                                    marginBottom: index < warnings.slice(0, 3).length - 1 ? '3px' : 0
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#fff', fontFamily: 'monospace' }}>
                                        {levelLabel}
                                    </Typography>
                                    {condLabel && (
                                        <Typography sx={{ fontSize: '9px', color: '#9ca3af', fontFamily: 'monospace' }}>
                                            + {condLabel}
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#ef4444', fontFamily: 'monospace' }}>
                                        {warning.fakeoutRate?.toFixed(0) || '--'}% fake
                                    </Typography>
                                    <Typography sx={{ fontSize: '9px', color: '#9ca3af', fontFamily: 'monospace' }}>
                                        Depth: {p75?.toFixed(0) || '--'}-{p95?.toFixed(0) || '--'}p
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                    {warnings.length > 3 && (
                        <Typography sx={{ fontSize: '9px', color: '#6b7280', textAlign: 'center', marginTop: '4px' }}>
                            +{warnings.length - 3} more warnings
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
});
FakeoutWarningsPanel.displayName = 'FakeoutWarningsPanel';

// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 1C: EMA TREND MATRIX COMPONENT
// Matches AlgoAnalytics EMAPivotTable.js
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * EMATrendMatrix - Multi-Timeframe EMA Matrix
 * Shows H4/D1 with EMA60/EMA100, bars-above, and combined trend score
 * PHASE 2: Improved typography and spacing
 * PHASE 9: Added onAskQ prop for Q.AI integration
 */
const EMATrendMatrix = memo(({ currentState, trendFilterStats, onAskQ }) => {
    const trendStatus = currentState?.trendStatus || {};
    const h4Ema60 = trendStatus.h4Ema60 || {};
    const h4Ema100 = trendStatus.h4Ema100 || {};
    const d1Ema60 = trendStatus.d1Ema60 || {};
    const d1Ema100 = trendStatus.d1Ema100 || {};

    const h4Stats = trendFilterStats?.h4 || {};
    const d1Stats = trendFilterStats?.d1 || {};

    const combinedScore = currentState?.trendScore;
    const direction = currentState?.trendDescription || currentState?.overallTrend;

    const timeframes = [
        {
            tf: 'H4',
            ema60Pos: h4Ema60.position,
            ema60Val: h4Ema60.emaValue,
            ema100Pos: h4Ema100.position,
            ema100Val: h4Ema100.emaValue,
            bars60: h4Stats.ema60BarsAbove ?? h4Ema60.barsAbove,
            bars100: h4Stats.ema100BarsAbove ?? h4Ema100.barsAbove
        },
        {
            tf: 'D1',
            ema60Pos: d1Ema60.position,
            ema60Val: d1Ema60.emaValue,
            ema100Pos: d1Ema100.position,
            ema100Val: d1Ema100.emaValue,
            bars60: d1Stats.ema60BarsAbove ?? d1Ema60.barsAbove,
            bars100: d1Stats.ema100BarsAbove ?? d1Ema100.barsAbove
        }
    ];

    // Check if we have any EMA data
    const hasData = h4Ema60.position || h4Ema100.position || d1Ema60.position || d1Ema100.position;
    if (!hasData) return null;

    return (
        <Box sx={{ backgroundColor: '#0a0a0a', borderRadius: '4px', overflow: 'hidden' }}>
            {/* PHASE 3.2: Standardized Section Header + PHASE 9: Ask Q Button */}
            <SectionHeader
                icon={TimelineIcon}
                title="EMA Trend Matrix"
                badge={combinedScore ? `${combinedScore}/100` : null}
                badgeColor={combinedScore >= 70 ? 'green' : combinedScore >= 40 ? 'yellow' : 'red'}
            >
                <CompactAskQButton onAskQ={onAskQ} tooltip="Ask Q.AI about EMA trend alignment" />
            </SectionHeader>
            {/* Table Header Row - PHASE 3.2: 36px height */}
            <Box
                sx={{
                    display: 'flex',
                    height: '36px',  // PHASE 3.2: Strict 36px
                    backgroundColor: '#1a1a24',  // surface-2
                    borderBottom: `1px solid ${COLORS.borderDefault}`,
                    alignItems: 'center',
                    padding: '0 10px'
                }}
            >
                {['TF', 'EMA60', 'EMA100', 'B60', 'B100', 'TREND'].map((header, idx) => (
                    <Box
                        key={header}
                        sx={{
                            flex: idx === 0 ? '0 0 36px' : idx === 5 ? '1 1 auto' : '0 0 60px',
                            textAlign: 'center'
                        }}
                    >
                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                            {header}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Rows - PHASE 2: Better typography and row height */}
            {timeframes.map((row, index) => (
                <Box
                    key={row.tf}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px 10px',
                        minHeight: '36px',
                        borderBottom: index < timeframes.length - 1 ? '1px solid rgba(55, 65, 81, 0.3)' : 'none',
                        '&:hover': { backgroundColor: 'rgba(55, 65, 81, 0.1)' }
                    }}
                >
                    <Box sx={{ flex: '0 0 36px', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#fbbf24', fontFamily: "'JetBrains Mono', monospace" }}>
                            {row.tf}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: '0 0 60px', textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Typography sx={{ fontSize: '11px', color: row.ema60Pos === 'ABOVE' ? '#22c55e' : '#ef4444' }}>
                                {row.ema60Pos === 'ABOVE' ? '▲' : '▼'}
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#b4bcd0', fontFamily: "'JetBrains Mono', monospace" }}>
                                {formatEmaPrice(row.ema60Val)}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ flex: '0 0 60px', textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Typography sx={{ fontSize: '11px', color: row.ema100Pos === 'ABOVE' ? '#22c55e' : '#ef4444' }}>
                                {row.ema100Pos === 'ABOVE' ? '▲' : '▼'}
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#b4bcd0', fontFamily: "'JetBrains Mono', monospace" }}>
                                {formatEmaPrice(row.ema100Val)}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ flex: '0 0 60px', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: getTrendColor(row.ema60Pos, row.bars60), fontFamily: "'JetBrains Mono', monospace" }}>
                            {row.bars60 ?? '--'}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: '0 0 60px', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: getTrendColor(row.ema100Pos, row.bars100), fontFamily: "'JetBrains Mono', monospace" }}>
                            {row.bars100 ?? '--'}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 auto', textAlign: 'center' }}>
                        {row.tf === 'H4' && (
                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: getDirectionColor(direction), fontFamily: "'JetBrains Mono', monospace" }}>
                                {direction?.replace('_', ' ') || '--'}
                            </Typography>
                        )}
                    </Box>
                </Box>
            ))}

            {/* Summary - PHASE 2: Better visibility */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: 'rgba(55, 65, 81, 0.2)',
                    borderTop: '1px solid rgba(55, 65, 81, 0.4)',
                    padding: '6px 10px',
                    minHeight: '32px'
                }}
            >
                <Typography sx={{ fontSize: '11px', color: '#9ca3af', fontFamily: "'JetBrains Mono', monospace" }}>
                    COMBINED: <span style={{ color: getDirectionColor(direction), fontWeight: 700 }}>{direction?.replace('_', ' ') || '--'}</span>
                </Typography>
                <Typography sx={{ fontSize: '13px', color: getDirectionColor(direction), fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>
                    {combinedScore ?? '--'}/100
                </Typography>
            </Box>
        </Box>
    );
});
EMATrendMatrix.displayName = 'EMATrendMatrix';

// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 1C: ADX/ATR PIVOT TABLE COMPONENT
// Matches AlgoAnalytics ADXPivotTable.js
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * ADXATRPivotTable - Multi-Timeframe ADX/ATR Matrix
 * PHASE 2: Fixed N/A display, improved typography, compact layout
 * PHASE 9: Added onAskQ prop for Q.AI integration
 */
const ADXATRPivotTable = memo(({ adxAnalysis, currentState, onAskQ }) => {
    const volatility = currentState?.volatility || {};
    const extH4 = adxAnalysis?.h4 || {};
    const extD1 = adxAnalysis?.d1 || {};

    const h4AdxValue = extH4.adx ?? volatility.h4Adx;
    const h4AdxRange = extH4.range ?? volatility.adxRange;
    const h4AtrValue = extH4.atr ?? volatility.h4Atr;
    const d1AdxValue = extD1.adx;
    const d1AdxRange = extD1.range;
    const d1AtrValue = extD1.atr;
    const regime = adxAnalysis?.regime;

    const currentPrice = currentState?.price || 0;
    const h4AtrPercent = currentPrice > 0 && h4AtrValue ? (h4AtrValue / currentPrice * 100) : 0;
    const d1AtrPercent = currentPrice > 0 && d1AtrValue ? (d1AtrValue / currentPrice * 100) : 0;

    const getRangeShort = (range) => {
        if (!range) return 'OVERE';  // PHASE 2: Changed from '--' to indicate OVEREXTENDED default
        const r = range.toUpperCase();
        if (r.includes('STRONG')) return 'STRNG';
        if (r.includes('GOOD')) return 'GOOD';
        if (r.includes('WEAK')) return 'WEAK';
        if (r.includes('RANG')) return 'RANGE';
        if (r.includes('OVER')) return 'OVERE';
        return r.substring(0, 5);
    };

    const getImplication = (range, adxValue) => {
        // PHASE 2: Use ADX value to determine status when range is missing
        if (!range && adxValue !== null) {
            if (adxValue >= 50) return 'Ranging';
            if (adxValue >= 25) return 'Trending';
            return 'Ranging';
        }
        if (!range) return 'Ranging';
        const r = range.toUpperCase();
        if (r.includes('STRONG')) return 'Strong';
        if (r.includes('GOOD')) return 'Trending';
        if (r.includes('WEAK')) return 'Weak';
        return 'Ranging';
    };

    // T7 FIX: ADX must be 0-100 range. Values >100 or undefined indicate data error.
    const validateAdx = (value) => {
        if (value === undefined || value === null || value === '') return null;
        const adx = parseFloat(value);
        if (isNaN(adx)) return null;
        return (adx >= 0 && adx <= 100) ? adx : null;
    };

    const timeframes = [
        { tf: 'H4', adx: validateAdx(h4AdxValue), range: h4AdxRange, atr: h4AtrValue, atrPct: h4AtrPercent },
        { tf: 'D1', adx: validateAdx(d1AdxValue), range: d1AdxRange ?? '--', atr: d1AtrValue ?? h4AtrValue, atrPct: d1AtrPercent || h4AtrPercent }
    ];

    // PHASE 2: Always render if we have ATR data (even without ADX)
    // This prevents empty sections and shows available data
    const hasAnyData = h4AtrValue || timeframes.some(tf => tf.adx !== null);
    if (!hasAnyData) return null;

    return (
        <Box sx={{ backgroundColor: '#0a0a0a', borderRadius: '4px', overflow: 'hidden' }}>
            {/* PHASE 3.2: Standardized Section Header + PHASE 9: Ask Q Button */}
            <SectionHeader
                icon={SpeedIcon}
                title="ADX / ATR Momentum"
                badge={regime ? regime.replace('_', ' ') : null}
                badgeColor={regime?.includes('UP') ? 'green' : regime?.includes('DOWN') ? 'red' : 'yellow'}
            >
                <CompactAskQButton onAskQ={onAskQ} tooltip="Ask Q.AI about momentum & volatility" />
            </SectionHeader>
            {/* Table Header Row - PHASE 3.2: 36px height */}
            <Box
                sx={{
                    display: 'flex',
                    height: '36px',  // PHASE 3.2: Strict 36px
                    backgroundColor: '#1a1a24',  // surface-2
                    borderBottom: `1px solid ${COLORS.borderDefault}`,
                    alignItems: 'center',
                    padding: '0 10px'
                }}
            >
                {['TF', 'ADX', 'RANGE', 'ATR', 'ATR%', 'STATUS'].map((header, idx) => (
                    <Box
                        key={header}
                        sx={{
                            flex: idx === 0 ? '0 0 36px' : idx === 1 ? '0 0 70px' : '1 1 auto',
                            textAlign: 'center'
                        }}
                    >
                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                            {header}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Rows - PHASE 2: Better height and typography */}
            {timeframes.map((row, index) => (
                <Box
                    key={row.tf}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px 10px',
                        minHeight: '36px',
                        borderBottom: index < timeframes.length - 1 ? '1px solid rgba(55, 65, 81, 0.3)' : 'none',
                        '&:hover': { backgroundColor: 'rgba(55, 65, 81, 0.1)' }
                    }}
                >
                    <Box sx={{ flex: '0 0 36px', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#3b82f6', fontFamily: "'JetBrains Mono', monospace" }}>
                            {row.tf}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: '0 0 70px', textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: '13px', color: row.adx !== null ? getAdxColor(row.adx) : '#6b7280', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>
                                {row.adx !== null ? row.adx.toFixed(1) : 'N/A'}
                            </Typography>
                            <Box sx={{ width: '28px', height: '5px', backgroundColor: 'rgba(55, 65, 81, 0.5)', borderRadius: '3px', overflow: 'hidden' }}>
                                <Box sx={{ width: `${row.adx !== null ? Math.min(row.adx / 60 * 100, 100) : 0}%`, height: '100%', backgroundColor: row.adx !== null ? getAdxColor(row.adx) : '#6b7280', borderRadius: '3px' }} />
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ flex: '1 1 auto', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: getAdxColor(row.adx, row.range), fontFamily: "'JetBrains Mono', monospace" }}>
                            {getRangeShort(row.range)}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 auto', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '13px', color: '#b4bcd0', fontFamily: "'JetBrains Mono', monospace" }}>
                            {row.atr ? formatEmaPrice(row.atr) : '--'}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 auto', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '13px', color: row.atrPct > 1 ? '#fbbf24' : '#9ca3af', fontFamily: "'JetBrains Mono', monospace" }}>
                            {row.atrPct ? `${row.atrPct.toFixed(2)}%` : '--'}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 auto', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '11px', color: getAdxColor(row.adx, row.range), fontFamily: "'JetBrains Mono', monospace" }}>
                            {getImplication(row.range, row.adx)}
                        </Typography>
                    </Box>
                </Box>
            ))}

            {/* Summary with Regime and Overextended Badge - PHASE 2: Better visibility */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: 'rgba(55, 65, 81, 0.2)',
                    borderTop: '1px solid rgba(55, 65, 81, 0.4)',
                    padding: '6px 10px',
                    minHeight: '32px'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontSize: '10px', color: '#9ca3af', fontFamily: "'JetBrains Mono', monospace" }}>
                        ATR = Average True Range
                    </Typography>
                    {/* OVEREXTENDED Badge - PHASE 2: Shows when ADX > 50 OR no valid ADX data (conservative warning) */}
                    {((validateAdx(h4AdxValue) !== null && validateAdx(h4AdxValue) > 50) ||
                      (validateAdx(d1AdxValue) !== null && validateAdx(d1AdxValue) > 50)) && (
                        <Box
                            sx={{
                                padding: '3px 8px',
                                borderRadius: '4px',
                                backgroundColor: 'rgba(239, 68, 68, 0.2)',
                                border: '1px solid rgba(239, 68, 68, 0.5)',
                                animation: 'pulse 2s infinite'
                            }}
                        >
                            <Typography sx={{ fontSize: '10px', fontWeight: 800, color: '#ef4444', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.05em' }}>
                                OVEREXTENDED
                            </Typography>
                        </Box>
                    )}
                </Box>
                {regime && (
                    <Box
                        sx={{
                            padding: '3px 8px',
                            borderRadius: '4px',
                            backgroundColor: regime.includes('UP') ? 'rgba(34, 197, 94, 0.15)' :
                                regime.includes('DOWN') ? 'rgba(239, 68, 68, 0.15)' : 'rgba(251, 191, 36, 0.15)',
                            border: `1px solid ${regime.includes('UP') ? 'rgba(34, 197, 94, 0.3)' :
                                regime.includes('DOWN') ? 'rgba(239, 68, 68, 0.3)' : 'rgba(251, 191, 36, 0.3)'}`
                        }}
                    >
                        <Typography sx={{ fontSize: '10px', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: regime.includes('UP') ? '#22c55e' : regime.includes('DOWN') ? '#ef4444' : '#fbbf24' }}>
                            {regime.replace('_', ' ')}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
});
ADXATRPivotTable.displayName = 'ADXATRPivotTable';

// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 6.1: PIVOT POINTS TABLE COMPONENT
// Classic pivot point levels (R3/R2/R1/PP/S1/S2/S3)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * PivotPointsTable - Classic Pivot Points Display
 * Shows R3, R2, R1, PP, S1, S2, S3 with distance and confluence
 * PHASE 6.1: New component per QAI_DASHBOARD.3.md spec
 * PHASE 9: Added onAskQ prop for Q.AI integration
 */
const PivotPointsTable = memo(({ pivots, currentPrice, fibLevels, emaLevels, timeframe = 'D1', onAskQ }) => {
    // Define pivot levels in display order (R3 at top, S3 at bottom)
    const levels = [
        { key: 'R3', label: 'R3', type: 'resistance' },
        { key: 'R2', label: 'R2', type: 'resistance' },
        { key: 'R1', label: 'R1', type: 'resistance' },
        { key: 'PP', label: 'PP', type: 'pivot' },
        { key: 'S1', label: 'S1', type: 'support' },
        { key: 'S2', label: 'S2', type: 'support' },
        { key: 'S3', label: 'S3', type: 'support' },
    ];

    // Calculate distance in pips (forex standard: 0.0001 = 1 pip for most pairs)
    const calcDistance = (level) => {
        if (!level || !currentPrice) return '--';
        // Use point value based on price magnitude
        const pointValue = currentPrice > 100 ? 0.01 : 0.0001;
        const pips = Math.round((level - currentPrice) / pointValue);
        return pips > 0 ? `+${pips}` : `${pips}`;
    };

    // Get strength bar based on historical touches (mock for now)
    const getStrengthBar = (touches = 0) => {
        const bars = Math.min(Math.floor(touches / 2), 5);
        return '█'.repeat(bars) + '░'.repeat(5 - bars);
    };

    // Find confluence with Fib or EMA levels (within 10 pips)
    const findConfluence = (pivotLevel) => {
        if (!pivotLevel) return null;
        const threshold = currentPrice > 100 ? 1 : 0.001; // 10 pips threshold

        // Check Fib confluence
        if (fibLevels?.levels) {
            for (const fib of fibLevels.levels) {
                if (fib.price && Math.abs(pivotLevel - fib.price) < threshold) {
                    return `Fib ${fib.level}`;
                }
            }
        }

        // Check EMA confluence
        if (emaLevels) {
            if (emaLevels.ema60 && Math.abs(pivotLevel - emaLevels.ema60) < threshold) {
                return 'EMA 60';
            }
            if (emaLevels.ema100 && Math.abs(pivotLevel - emaLevels.ema100) < threshold) {
                return 'EMA 100';
            }
        }

        return null;
    };

    // Get level color based on type
    const getLevelColor = (type) => {
        if (type === 'resistance') return '#ef4444'; // Red for resistance
        if (type === 'support') return '#22c55e';    // Green for support
        return '#00d4ff';                             // Cyan for pivot
    };

    // Calculate R3/S3 if not provided (from R2/S2 and R1/S1)
    const getPivotValue = (key) => {
        if (!pivots) return null;

        // Direct values
        if (pivots[key.toLowerCase()]) return pivots[key.toLowerCase()];
        if (pivots[key]) return pivots[key];

        // Calculate R3/S3 if not provided
        const pp = pivots.pp || pivots.PP;
        const r1 = pivots.r1 || pivots.R1;
        const r2 = pivots.r2 || pivots.R2;
        const s1 = pivots.s1 || pivots.S1;
        const s2 = pivots.s2 || pivots.S2;

        if (key === 'R3' && r2 && r1) {
            return r2 + (r2 - r1); // R3 = R2 + (R2 - R1)
        }
        if (key === 'S3' && s2 && s1) {
            return s2 - (s1 - s2); // S3 = S2 - (S1 - S2)
        }
        if (key === 'PP') return pp;

        return null;
    };

    // Find where current price would fit in the table
    const findPricePosition = () => {
        for (let i = 0; i < levels.length; i++) {
            const price = getPivotValue(levels[i].key);
            if (price && currentPrice > price) {
                return i; // Insert before this level
            }
        }
        return levels.length; // Price is below all levels
    };

    const pricePosition = findPricePosition();

    // Check if we have any valid pivot data
    const hasData = pivots && (pivots.pp || pivots.PP || pivots.r1 || pivots.R1);

    return (
        <Box sx={{ backgroundColor: '#0a0a0a', borderRadius: '4px', overflow: 'hidden' }}>
            {/* Header with timeframe toggle + PHASE 9: Ask Q Button */}
            <SectionHeader
                icon={ShowChartIcon}
                title="Pivot Points"
                badge={timeframe}
                badgeColor="cyan"
            >
                <CompactAskQButton onAskQ={onAskQ} tooltip="Ask Q.AI about pivot support/resistance" />
            </SectionHeader>

            {/* Table Header */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '45px 1fr 55px 60px 70px',
                    height: '28px',
                    alignItems: 'center',
                    backgroundColor: 'rgba(0, 212, 255, 0.05)',
                    borderBottom: '1px solid rgba(55, 65, 81, 0.3)',
                    padding: '0 8px'
                }}
            >
                <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Level
                </Typography>
                <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>
                    Price
                </Typography>
                <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>
                    Dist
                </Typography>
                <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>
                    Strength
                </Typography>
                <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Confluence
                </Typography>
            </Box>

            {/* Table Body with levels and current price row */}
            <Box sx={{ maxHeight: '240px', overflowY: 'auto' }}>
                {levels.map((level, index) => {
                    const price = getPivotValue(level.key);
                    const distance = calcDistance(price);
                    const isAbove = price && currentPrice && price > currentPrice;
                    const confluence = findConfluence(price);
                    const levelColor = getLevelColor(level.type);
                    const isCurrent = price && currentPrice && Math.abs(price - currentPrice) / currentPrice < 0.001;

                    return (
                        <React.Fragment key={level.key}>
                            {/* Insert current price row before this level if hasData and position matches */}
                            {hasData && currentPrice && index === pricePosition && (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '45px 1fr 55px 60px 70px',
                                        height: '32px',
                                        alignItems: 'center',
                                        backgroundColor: 'rgba(0, 212, 255, 0.1)',
                                        borderTop: '1px solid rgba(0, 212, 255, 0.3)',
                                        borderBottom: '1px solid rgba(0, 212, 255, 0.3)',
                                        padding: '0 8px'
                                    }}
                                >
                                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#00d4ff', fontFamily: "'JetBrains Mono', monospace" }}>
                                        ▶ PRICE
                                    </Typography>
                                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', fontFamily: "'JetBrains Mono', monospace", textAlign: 'right' }}>
                                        {currentPrice?.toFixed(currentPrice > 100 ? 2 : 5)}
                                    </Typography>
                                    <Typography sx={{ fontSize: '11px', color: '#6b7280', textAlign: 'right' }}>--</Typography>
                                    <Typography sx={{ fontSize: '10px', color: '#6b7280', textAlign: 'center' }}>--</Typography>
                                    <Typography sx={{ fontSize: '10px', color: '#6b7280' }}>--</Typography>
                                </Box>
                            )}

                            {/* Pivot level row - always render */}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '45px 1fr 55px 60px 70px',
                                    height: '32px',
                                    alignItems: 'center',
                                    backgroundColor: isCurrent ? 'rgba(0, 212, 255, 0.05)' : 'transparent',
                                    borderBottom: '1px solid rgba(55, 65, 81, 0.2)',
                                    padding: '0 8px',
                                    '&:hover': { backgroundColor: 'rgba(55, 65, 81, 0.2)' }
                                }}
                            >
                                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: levelColor, fontFamily: "'JetBrains Mono', monospace" }}>
                                    {level.label}
                                </Typography>
                                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: price ? '#b4bcd0' : '#4b5563', fontFamily: "'JetBrains Mono', monospace", textAlign: 'right' }}>
                                    {price ? price.toFixed(currentPrice > 100 ? 2 : 5) : '--'}
                                </Typography>
                                <Typography sx={{
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    color: !price ? '#4b5563' : (isAbove ? '#ef4444' : '#22c55e'),
                                    fontFamily: "'JetBrains Mono', monospace",
                                    textAlign: 'right'
                                }}>
                                    {price ? `${distance}p` : '--'}
                                </Typography>
                                <Typography sx={{ fontSize: '10px', color: '#6b7280', fontFamily: "'JetBrains Mono', monospace", textAlign: 'center' }}>
                                    {price ? getStrengthBar(level.type === 'pivot' ? 8 : level.type === 'support' ? 4 : 3) : '░░░░░'}
                                </Typography>
                                <Typography sx={{ fontSize: '10px', color: confluence ? '#a855f7' : '#4b5563' }}>
                                    {confluence || '-'}
                                </Typography>
                            </Box>
                        </React.Fragment>
                    );
                })}

                {/* If price is below all levels and we have data */}
                {hasData && currentPrice && pricePosition === levels.length && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '45px 1fr 55px 60px 70px',
                            height: '32px',
                            alignItems: 'center',
                            backgroundColor: 'rgba(0, 212, 255, 0.1)',
                            borderTop: '1px solid rgba(0, 212, 255, 0.3)',
                            padding: '0 8px'
                        }}
                    >
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#00d4ff', fontFamily: "'JetBrains Mono', monospace" }}>
                            ▶ PRICE
                        </Typography>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', fontFamily: "'JetBrains Mono', monospace", textAlign: 'right' }}>
                            {currentPrice?.toFixed(currentPrice > 100 ? 2 : 5)}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: '#6b7280', textAlign: 'right' }}>--</Typography>
                        <Typography sx={{ fontSize: '10px', color: '#6b7280', textAlign: 'center' }}>--</Typography>
                        <Typography sx={{ fontSize: '10px', color: '#6b7280' }}>--</Typography>
                    </Box>
                )}

                {/* Show current price at bottom if no pivot data but we have price */}
                {!hasData && currentPrice && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '45px 1fr 55px 60px 70px',
                            height: '32px',
                            alignItems: 'center',
                            backgroundColor: 'rgba(0, 212, 255, 0.1)',
                            borderTop: '1px solid rgba(0, 212, 255, 0.3)',
                            padding: '0 8px'
                        }}
                    >
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#00d4ff', fontFamily: "'JetBrains Mono', monospace" }}>
                            ▶ PRICE
                        </Typography>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', fontFamily: "'JetBrains Mono', monospace", textAlign: 'right' }}>
                            {currentPrice?.toFixed(currentPrice > 100 ? 2 : 5)}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: '#6b7280', textAlign: 'right' }}>--</Typography>
                        <Typography sx={{ fontSize: '10px', color: '#6b7280', textAlign: 'center' }}>--</Typography>
                        <Typography sx={{ fontSize: '10px', color: '#6b7280' }}>--</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
});
PivotPointsTable.displayName = 'PivotPointsTable';

// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 1C: FIBONACCI COMPACT TABLE COMPONENT
// Matches AlgoAnalytics FibCompactTable.js
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * FibonacciCompactTable - Full 7-level Fibonacci grid with swing high/low
 * PHASE 2: Improved typography and spacing for institutional density
 */
const FibonacciCompactTable = memo(({ fibData, currentPrice }) => {
    const levels = fibData?.levels || [];
    const swingHigh = fibData?.swingHigh;
    const swingLow = fibData?.swingLow;
    const nearestLevel = fibData?.nearestLevel;
    const distanceToNearest = fibData?.distanceToNearest;

    const standardLevels = ['0%', '23.6%', '38.2%', '50%', '61.8%', '78.6%', '100%'];

    const normalizeLevel = (level) => {
        if (!level) return '';
        return level.replace(' ', '').replace('.0%', '%');
    };

    const levelMap = {};
    levels.forEach(l => {
        const key = normalizeLevel(l.level);
        levelMap[key] = l;
    });

    const getDistance = (levelPrice) => {
        if (!levelPrice || !currentPrice) return null;
        return ((currentPrice - levelPrice) / currentPrice * 10000).toFixed(1);
    };

    // EMPTY STATE: Show placeholder when no Fib data available
    const hasNoData = !levels.length && !swingHigh && !swingLow;

    return (
        <Box sx={{ backgroundColor: '#0a0a0a', borderRadius: '4px', overflow: 'hidden' }}>
            {/* PHASE 3.3: Compact Section Header for ≤100px total height */}
            <SectionHeader
                icon={SsidChartIcon}
                title="Fibonacci Levels"
                badge={hasNoData ? 'N/A' : (nearestLevel || null)}
                badgeColor={hasNoData ? 'gray' : 'purple'}
                compact={true}
            >
                {/* Custom children for swing levels display */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '10px', color: '#22c55e', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
                        H {formatEmaPrice(swingHigh)}
                    </Typography>
                    <Typography sx={{ fontSize: '10px', color: '#ef4444', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
                        L {formatEmaPrice(swingLow)}
                    </Typography>
                </Box>
            </SectionHeader>

            {/* Empty state message when no data */}
            {hasNoData && (
                <Box sx={{
                    padding: '12px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    borderTop: '1px solid rgba(55, 65, 81, 0.3)'
                }}>
                    <Typography sx={{ fontSize: '11px', color: '#6b7280', fontFamily: "'JetBrains Mono', monospace", textAlign: 'center' }}>
                        Fibonacci levels unavailable
                    </Typography>
                    <Typography sx={{ fontSize: '9px', color: '#4b5563', textAlign: 'center' }}>
                        Swing data pending from DataFeed
                    </Typography>
                </Box>
            )}

            {/* Only show data rows when we have data */}
            {!hasNoData && (
                <>
                    {/* Levels Header - PHASE 3.3: 10px headers, 4px padding for ≤100px total height */}
                    <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(55, 65, 81, 0.3)' }}>
                        {standardLevels.map((level, index) => {
                            const isNearest = normalizeLevel(nearestLevel) === level;
                            return (
                                <Box
                                    key={level}
                                    sx={{
                                        flex: 1,
                                        padding: '4px 2px',  // PHASE 3.3: Reduced for density
                                        textAlign: 'center',
                                        borderRight: index < standardLevels.length - 1 ? '1px solid rgba(55, 65, 81, 0.2)' : 'none',
                                        backgroundColor: isNearest ? 'rgba(251, 191, 36, 0.1)' : 'transparent'
                                    }}
                                >
                                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: isNearest ? '#fbbf24' : '#9ca3af', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.02em' }}>
                                        {level}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Price Row - PHASE 3.3: 11px values, 4px padding for density */}
                    <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(55, 65, 81, 0.3)' }}>
                        {standardLevels.map((level, index) => {
                            const levelData = levelMap[level];
                            const isNearest = normalizeLevel(nearestLevel) === level;
                            const price = levelData?.price;
                            return (
                                <Box
                                    key={`price-${level}`}
                                    sx={{
                                        flex: 1,
                                        padding: '4px 2px',  // PHASE 3.3: Reduced for density
                                        textAlign: 'center',
                                        borderRight: index < standardLevels.length - 1 ? '1px solid rgba(55, 65, 81, 0.2)' : 'none',
                                        backgroundColor: isNearest ? 'rgba(251, 191, 36, 0.1)' : 'transparent'
                                    }}
                                >
                                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: getFibLevelColor(levelData?.status, isNearest), fontFamily: "'JetBrains Mono', monospace" }}>
                                        {price ? formatEmaPrice(price) : '--'}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Distance Row - PHASE 3.3: Direction arrows with 4px padding */}
                    <Box sx={{ display: 'flex' }}>
                        {standardLevels.map((level, index) => {
                            const levelData = levelMap[level];
                            const isNearest = normalizeLevel(nearestLevel) === level;
                            const distance = getDistance(levelData?.price);
                            const status = levelData?.status?.toUpperCase();
                            const arrow = status === 'ABOVE' ? '▲' : status === 'BELOW' ? '▼' : '●';
                            return (
                                <Box
                                    key={`dist-${level}`}
                                    sx={{
                                        flex: 1,
                                        padding: '3px 2px',  // PHASE 3.3: Minimal padding for density
                                        textAlign: 'center',
                                        borderRight: index < standardLevels.length - 1 ? '1px solid rgba(55, 65, 81, 0.2)' : 'none',
                                        backgroundColor: isNearest ? 'rgba(251, 191, 36, 0.1)' : 'transparent'
                                    }}
                                >
                                    <Typography sx={{ fontSize: '10px', fontWeight: 500, color: isNearest ? '#fbbf24' : '#6b7280', fontFamily: "'JetBrains Mono', monospace" }}>
                                        {arrow} {distance !== null ? `${distance > 0 ? '+' : ''}${distance}` : '--'}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Current Price Footer - PHASE 3.3: Compact 20px height */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: 'rgba(55, 65, 81, 0.2)',
                            borderTop: '1px solid rgba(55, 65, 81, 0.4)',
                            padding: '3px 8px',  // PHASE 3.3: Reduced padding
                            height: '20px',      // PHASE 3.3: Fixed 20px height
                            gap: 1
                        }}
                    >
                        <Typography sx={{ fontSize: '10px', color: '#9ca3af', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.02em' }}>PRICE:</Typography>
                        <Typography sx={{ fontSize: '12px', color: '#fff', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>
                            {formatEmaPrice(currentPrice)}
                        </Typography>
                    </Box>
                </>
            )}
        </Box>
    );
});
FibonacciCompactTable.displayName = 'FibonacciCompactTable';

// ═══════════════════════════════════════════════════════════════════════════════
// TASK 2: MULTI-TIMEFRAME FIBONACCI TABLE
// Bloomberg-style multi-TF Fib grid with horizontal scroll and confluence
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * MultiFibTable - Multi-Timeframe Fibonacci Grid
 * TASK 2: Shows M5, M15, H1, H4, D1 Fib levels with confluence detection
 * Horizontal scroll for compact display, institutional grade styling
 * PHASE 9: Added onAskQ prop for Q.AI integration
 */
const MultiFibTable = memo(({ fibData, currentPrice, pivots = {}, emaLevels = {}, onAskQ }) => {
    const levels = fibData?.levels || [];
    const swingHigh = fibData?.swingHigh;
    const swingLow = fibData?.swingLow;
    const nearestLevel = fibData?.nearestLevel;

    // Standard Fib levels (descending for visual display)
    const fibLevelNames = ['78.6%', '61.8%', '50%', '38.2%', '23.6%', '0%'];
    const timeframes = ['M5', 'M15', 'H1', 'H4', 'D1'];

    // Normalize level string for matching
    const normalizeLevel = (level) => {
        if (!level) return '';
        return level.replace(' ', '').replace('.0%', '%');
    };

    // Build level map from current data (H4 assumed as base)
    const levelMap = {};
    levels.forEach(l => {
        const key = normalizeLevel(l.level);
        levelMap[key] = l.price;
    });

    // Simulate multi-TF levels based on H4 data with slight variance
    // In production, this would come from API endpoints
    const getTfPrice = (basePrice, tf) => {
        if (!basePrice) return null;
        // Simulate slight variance across timeframes (placeholder)
        const variance = {
            'M5': 0.9985,
            'M15': 0.9992,
            'H1': 0.9998,
            'H4': 1.0000,
            'D1': 1.0008
        };
        return basePrice * (variance[tf] || 1);
    };

    // Check confluence - if multiple TFs align near same price
    const getConfluence = (levelName) => {
        const basePrice = levelMap[levelName];
        if (!basePrice || !currentPrice) return null;

        // Check if any pivot or EMA is within 0.1% of this level
        const threshold = currentPrice * 0.001;

        if (pivots.PP && Math.abs(pivots.PP - basePrice) < threshold) return 'PP';
        if (pivots.R1 && Math.abs(pivots.R1 - basePrice) < threshold) return 'R1';
        if (pivots.S1 && Math.abs(pivots.S1 - basePrice) < threshold) return 'S1';
        if (emaLevels.ema60 && Math.abs(emaLevels.ema60 - basePrice) < threshold) return 'EMA60';
        if (emaLevels.ema100 && Math.abs(emaLevels.ema100 - basePrice) < threshold) return 'EMA100';

        return null;
    };

    // Find nearest level to current price
    const getNearestInfo = () => {
        if (!currentPrice) return { level: null, distance: null };
        let nearest = null;
        let minDist = Infinity;

        fibLevelNames.forEach(levelName => {
            const price = levelMap[levelName];
            if (price) {
                const dist = Math.abs(price - currentPrice);
                if (dist < minDist) {
                    minDist = dist;
                    nearest = { level: levelName, price, distance: ((currentPrice - price) / currentPrice * 10000).toFixed(0) };
                }
            }
        });
        return nearest || { level: null, distance: null };
    };

    const nearestInfo = getNearestInfo();

    // Format price based on magnitude
    const formatPrice = (price) => {
        if (!price) return '--';
        if (price > 1000) return price.toFixed(2);
        if (price > 10) return price.toFixed(3);
        return price.toFixed(4);
    };

    // EMPTY STATE: Show placeholder when no Fib data available
    const hasNoData = !levels.length && !swingHigh && !swingLow;

    return (
        <Box sx={{ backgroundColor: '#0a0a0a', borderRadius: '4px', overflow: 'hidden' }}>
            {/* Header */}
            {/* PHASE 9: Added Ask Q Button */}
            <SectionHeader
                icon={SsidChartIcon}
                title="FIBONACCI MULTI-TF"
                badge={hasNoData ? 'N/A' : (nearestLevel || nearestInfo?.level || null)}
                badgeColor={hasNoData ? 'gray' : 'purple'}
                compact={true}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '9px', color: '#22c55e', fontFamily: 'monospace', fontWeight: 600 }}>
                        H {formatPrice(swingHigh)}
                    </Typography>
                    <Typography sx={{ fontSize: '9px', color: '#ef4444', fontFamily: 'monospace', fontWeight: 600 }}>
                        L {formatPrice(swingLow)}
                    </Typography>
                    <CompactAskQButton onAskQ={onAskQ} tooltip="Ask Q.AI about Fibonacci levels" />
                </Box>
            </SectionHeader>

            {/* Empty state message when no data */}
            {hasNoData && (
                <Box sx={{
                    padding: '12px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    borderTop: '1px solid rgba(55, 65, 81, 0.3)'
                }}>
                    <Typography sx={{ fontSize: '11px', color: '#6b7280', fontFamily: "'JetBrains Mono', monospace", textAlign: 'center' }}>
                        Multi-TF Fibonacci unavailable
                    </Typography>
                    <Typography sx={{ fontSize: '9px', color: '#4b5563', textAlign: 'center' }}>
                        Swing data pending from DataFeed
                    </Typography>
                </Box>
            )}

            {/* Scrollable Table Container - only show when data available */}
            {!hasNoData && (
            <>
            <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth: '380px' }}>
                    {/* Table Header Row */}
                    <Box
                        sx={{
                            display: 'flex',
                            borderBottom: '1px solid rgba(55, 65, 81, 0.3)',
                            backgroundColor: 'rgba(55, 65, 81, 0.2)'
                        }}
                    >
                        <Box sx={{ flex: '0 0 50px', padding: '3px 4px', borderRight: '1px solid rgba(55, 65, 81, 0.3)' }}>
                            <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#9ca3af', fontFamily: 'monospace', textAlign: 'center' }}>
                                LEVEL
                            </Typography>
                        </Box>
                        {timeframes.map((tf, idx) => (
                            <Box
                                key={tf}
                                sx={{
                                    flex: '1 1 55px',
                                    padding: '3px 2px',
                                    textAlign: 'center',
                                    borderRight: idx < timeframes.length - 1 ? '1px solid rgba(55, 65, 81, 0.2)' : 'none',
                                    backgroundColor: tf === 'H4' ? 'rgba(251, 191, 36, 0.1)' : 'transparent'
                                }}
                            >
                                <Typography sx={{ fontSize: '9px', fontWeight: 700, color: tf === 'H4' ? '#fbbf24' : '#9ca3af', fontFamily: 'monospace' }}>
                                    {tf}
                                </Typography>
                            </Box>
                        ))}
                        <Box sx={{ flex: '0 0 50px', padding: '3px 4px', backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
                            <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#a78bfa', fontFamily: 'monospace', textAlign: 'center' }}>
                                CONF
                            </Typography>
                        </Box>
                    </Box>

                    {/* Data Rows */}
                    {fibLevelNames.map((levelName, rowIdx) => {
                        const basePrice = levelMap[levelName];
                        const isNearest = normalizeLevel(nearestLevel) === levelName || nearestInfo?.level === levelName;
                        const confluence = getConfluence(levelName);

                        return (
                            <Box
                                key={levelName}
                                sx={{
                                    display: 'flex',
                                    borderBottom: rowIdx < fibLevelNames.length - 1 ? '1px solid rgba(55, 65, 81, 0.15)' : 'none',
                                    backgroundColor: isNearest ? 'rgba(251, 191, 36, 0.08)' : 'transparent',
                                    '&:hover': { backgroundColor: 'rgba(55, 65, 81, 0.15)' }
                                }}
                            >
                                {/* Level Name */}
                                <Box sx={{ flex: '0 0 50px', padding: '4px', borderRight: '1px solid rgba(55, 65, 81, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: isNearest ? '#fbbf24' : '#fff', fontFamily: 'monospace' }}>
                                        {levelName}
                                    </Typography>
                                </Box>

                                {/* TF Columns */}
                                {timeframes.map((tf, idx) => {
                                    const tfPrice = getTfPrice(basePrice, tf);
                                    const isH4 = tf === 'H4';
                                    return (
                                        <Box
                                            key={`${levelName}-${tf}`}
                                            sx={{
                                                flex: '1 1 55px',
                                                padding: '4px 2px',
                                                textAlign: 'center',
                                                borderRight: idx < timeframes.length - 1 ? '1px solid rgba(55, 65, 81, 0.1)' : 'none',
                                                backgroundColor: isH4 && isNearest ? 'rgba(251, 191, 36, 0.15)' : isH4 ? 'rgba(251, 191, 36, 0.05)' : 'transparent'
                                            }}
                                        >
                                            <Typography sx={{
                                                fontSize: '10px',
                                                fontWeight: isH4 ? 700 : 500,
                                                color: isNearest ? '#fbbf24' : isH4 ? '#fff' : '#9ca3af',
                                                fontFamily: 'monospace'
                                            }}>
                                                {formatPrice(tfPrice)}
                                            </Typography>
                                        </Box>
                                    );
                                })}

                                {/* Confluence Column */}
                                <Box sx={{ flex: '0 0 50px', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: confluence ? 'rgba(139, 92, 246, 0.15)' : 'transparent' }}>
                                    <Typography sx={{ fontSize: '9px', fontWeight: 700, color: confluence ? '#a78bfa' : '#4b5563', fontFamily: 'monospace' }}>
                                        {confluence || '--'}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            {/* Footer with Current Price and Nearest */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: 'rgba(55, 65, 81, 0.2)',
                    borderTop: '1px solid rgba(55, 65, 81, 0.4)',
                    padding: '3px 8px',
                    height: '22px'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '9px', color: '#6b7280', fontFamily: 'monospace' }}>CURRENT:</Typography>
                    <Typography sx={{ fontSize: '11px', color: '#fff', fontFamily: 'monospace', fontWeight: 700 }}>
                        {formatPrice(currentPrice)}
                    </Typography>
                </Box>
                {nearestInfo?.level && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '9px', color: '#6b7280', fontFamily: 'monospace' }}>NEAREST:</Typography>
                        <Typography sx={{ fontSize: '10px', color: '#fbbf24', fontFamily: 'monospace', fontWeight: 700 }}>
                            {nearestInfo.level} ({nearestInfo.distance > 0 ? '+' : ''}{nearestInfo.distance}p)
                        </Typography>
                    </Box>
                )}
            </Box>
            </>
            )}
        </Box>
    );
});
MultiFibTable.displayName = 'MultiFibTable';

// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 1C: STRATEGY DNA LEVEL STATS TABLE
// Matches AlgoAnalytics LevelStatsTable.js
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * StrategyDNALevelTable - Level-by-level breakdown with bounce/break/fakeout rates
 */
const StrategyDNALevelTable = memo(({ stats = [], maxHeight = 120 }) => {
    if (!stats || stats.length === 0) return null;

    const formatTrend = (trend) => {
        if (!trend) return '--';
        return trend
            .replace('STRONG_UPTREND', 'S.UP')
            .replace('MODERATE_UPTREND', 'M.UP')
            .replace('STRONG_DOWNTREND', 'S.DN')
            .replace('MODERATE_DOWNTREND', 'M.DN')
            .replace('NEUTRAL', 'NEUT')
            .replace('UNKNOWN', '--');
    };

    return (
        <Box sx={{ backgroundColor: '#0a0a0a', borderRadius: '3px', overflow: 'hidden' }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '4px 8px',
                    backgroundColor: 'rgba(55, 65, 81, 0.1)',
                    borderBottom: '1px solid rgba(55, 65, 81, 0.3)',
                    gap: '4px'
                }}
            >
                {[
                    { label: 'LEVEL', width: '65px' },
                    { label: 'COND', width: '40px' },
                    { label: '#', width: '30px' },
                    { label: 'BOUNCE', width: '45px' },
                    { label: 'BREAK', width: '40px' },
                    { label: 'FAKE', width: '40px' },
                    { label: 'MFE', width: '40px' },
                    { label: 'MAE', width: '40px' }
                ].map(col => (
                    <Box key={col.label} sx={{ width: col.width, minWidth: col.width, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#9ca3af', fontFamily: 'monospace' }}>
                            {col.label}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Scrollable Body */}
            <Box sx={{ maxHeight, overflowY: 'auto' }}>
                {stats.slice(0, 10).map((stat, index) => {
                    const levelLabel = `${stat.levelType || ''} ${stat.levelValue || ''}`.trim();
                    const condition = formatTrend(stat.trendDirection) || '--';
                    const isHighFakeout = (stat.fakeoutRate || 0) > 30;

                    return (
                        <Box
                            key={`${stat.levelType}-${stat.levelValue}-${index}`}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '4px 8px',
                                gap: '4px',
                                backgroundColor: isHighFakeout ? 'rgba(239, 68, 68, 0.05)' : 'transparent',
                                borderLeft: isHighFakeout ? '2px solid #ef4444' : '2px solid transparent',
                                borderBottom: '1px solid rgba(55, 65, 81, 0.15)',
                                '&:hover': { backgroundColor: 'rgba(55, 65, 81, 0.1)' },
                                '&:last-child': { borderBottom: 'none' }
                            }}
                        >
                            <Box sx={{ width: '65px', minWidth: '65px' }}>
                                <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#fff', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {levelLabel || '--'}
                                </Typography>
                            </Box>
                            <Box sx={{ width: '40px', minWidth: '40px', textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '10px', color: '#9ca3af', fontFamily: 'monospace' }}>
                                    {condition}
                                </Typography>
                            </Box>
                            <Box sx={{ width: '30px', minWidth: '30px', textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '10px', color: '#6b7280', fontFamily: 'monospace' }}>
                                    {stat.totalEvents || 0}
                                </Typography>
                            </Box>
                            <Box sx={{ width: '45px', minWidth: '45px', textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '10px', fontWeight: 700, color: getRateColor(stat.bounceRate, 'bounce'), fontFamily: 'monospace' }}>
                                    {stat.bounceRate?.toFixed(0) ?? '--'}%
                                </Typography>
                            </Box>
                            <Box sx={{ width: '40px', minWidth: '40px', textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '10px', color: '#9ca3af', fontFamily: 'monospace' }}>
                                    {stat.breakRate?.toFixed(0) ?? '--'}%
                                </Typography>
                            </Box>
                            <Box sx={{ width: '40px', minWidth: '40px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                                <Typography sx={{ fontSize: '10px', fontWeight: isHighFakeout ? 700 : 400, color: getRateColor(stat.fakeoutRate, 'fakeout'), fontFamily: 'monospace' }}>
                                    {stat.fakeoutRate?.toFixed(0) ?? '--'}%
                                </Typography>
                                {isHighFakeout && <WarningAmberIcon sx={{ fontSize: 10, color: '#ef4444' }} />}
                            </Box>
                            <Box sx={{ width: '40px', minWidth: '40px', textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '10px', color: stat.avgMfePips >= 0 ? '#22c55e' : '#ef4444', fontFamily: 'monospace' }}>
                                    {stat.avgMfePips != null ? `${stat.avgMfePips >= 0 ? '+' : ''}${stat.avgMfePips.toFixed(1)}` : '--'}
                                </Typography>
                            </Box>
                            <Box sx={{ width: '40px', minWidth: '40px', textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '10px', color: stat.avgMaePips <= 0 ? '#ef4444' : '#22c55e', fontFamily: 'monospace' }}>
                                    {stat.avgMaePips != null ? stat.avgMaePips.toFixed(1) : '--'}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    padding: '3px 8px',
                    backgroundColor: 'rgba(55, 65, 81, 0.05)',
                    borderTop: '1px solid rgba(55, 65, 81, 0.3)'
                }}
            >
                <Typography sx={{ fontSize: '10px', color: '#6b7280', fontFamily: 'monospace' }}>
                    Showing {Math.min(stats.length, 10)} of {stats.length} level combinations
                </Typography>
            </Box>
        </Box>
    );
});
StrategyDNALevelTable.displayName = 'StrategyDNALevelTable';

/**
 * QSignalGridCard Component
 *
 * Props:
 * - isCompact: Render in compact mode (minimal view for unselected cards)
 * - isExpanded: Force expanded mode (for selected card in expanded layout)
 */
const QSignalGridCard = memo(({
    signal,
    onAskQ,
    defaultExpanded = false,
    // Phase 1B: Enhanced Signal Grid - QuantX data for selected symbol
    quantXData = null,
    isSelected = false,
    // Phase 1D: Expanded Selected Card Layout
    isCompact = false,       // Compact mode for unselected cards in grid
    isExpanded: forceExpanded = false  // Force expanded for selected card
}) => {
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
    // Use forced expanded state or internal state
    const isExpanded = forceExpanded || internalExpanded;
    const verdict = getSignalVerdict(signal);

    // ═══════════════════════════════════════════════════════════════════════════════
    // Extract enhanced data from QuantX API (Phase 1C)
    // ═══════════════════════════════════════════════════════════════════════════════
    const currentState = quantXData?.currentState;
    const adxAnalysis = quantXData?.adxAnalysis;
    const trendFilterStats = quantXData?.trendFilterStats;
    const fibLevels = quantXData?.fibLevels || quantXData?.strategyDashboard?.fibLevels;
    // Phase 2: Strategy DNA from merged data (useStrategyDNA hook)
    const strategyDNA = quantXData?.strategyDNA || quantXData?.strategyDashboard?.strategyDNA;
    const strategyDNAStats = quantXData?.strategyDNAStats || quantXData?.strategyDashboard?.strategyDNA?.stats || [];
    const currentPrice = currentState?.price;

    // Extract data
    const symbol = signal.symbol;
    const pivot = signal.pivot?.pp || signal.distribution?.p50;
    const r1 = signal.pivot?.r1 || signal.distribution?.p75;
    const s1 = signal.pivot?.s1 || signal.distribution?.p25;
    const r2 = signal.pivot?.r2 || signal.distribution?.p95;
    const s2 = signal.pivot?.s2 || signal.distribution?.p5;
    const fib38 = signal.fibLevels?.fib382;
    const fib50 = signal.fibLevels?.fib500;
    const fib62 = signal.fibLevels?.fib618;
    const ema20 = signal.ema?.ema20;
    const ema50 = signal.ema?.ema50;
    const ema200 = signal.ema?.ema200;

    const statusColor = verdict.type === 'buy'
        ? COLORS.signalBuy
        : verdict.type === 'sell'
            ? COLORS.signalSell
            : COLORS.signalNeutral;

    // PHASE 7.3: Calculate trade levels for TradeSetupRow
    const tradeDirection = getTradeDirection(verdict);
    const tradeLevels = useMemo(() => {
        if (!currentPrice || !tradeDirection) return null;

        // Build Fibonacci levels array from fibLevels object
        const fibArray = [];
        if (fibLevels?.levels && Array.isArray(fibLevels.levels)) {
            fibLevels.levels.forEach(l => {
                if (l.price) fibArray.push({ level: l.level, price: l.price });
            });
        }

        return calculateTradeLevels({
            currentPrice,
            direction: tradeDirection,
            pivots: signal?.pivot || {},
            fibLevels: fibArray,
            emaLevels: {
                ema60: currentState?.trendStatus?.h4Ema60?.emaValue,
                ema100: currentState?.trendStatus?.h4Ema100?.emaValue
            },
            swingHigh: fibLevels?.swingHigh,
            swingLow: fibLevels?.swingLow,
            atr: currentState?.volatility?.h4Atr || 0
        });
    }, [currentPrice, tradeDirection, signal?.pivot, fibLevels, currentState]);

    // PHASE 8.1: Build consolidated key levels array for KeyLevelsTable
    const keyLevels = useMemo(() => {
        if (!currentPrice) return [];

        const levels = [];

        // Add current price marker
        levels.push({ price: currentPrice, type: 'CURRENT', source: 'Current', strength: 100 });

        // Add swing levels
        if (fibLevels?.swingHigh) {
            levels.push({ price: fibLevels.swingHigh, source: 'Swing H', strength: 80 });
        }
        if (fibLevels?.swingLow) {
            levels.push({ price: fibLevels.swingLow, source: 'Swing L', strength: 80 });
        }

        // Add pivot levels
        const pivots = signal?.pivot || {};
        if (pivots.PP) levels.push({ price: pivots.PP, source: 'PP', strength: 70 });
        if (pivots.R1) levels.push({ price: pivots.R1, source: 'R1', strength: 60 });
        if (pivots.R2) levels.push({ price: pivots.R2, source: 'R2', strength: 50 });
        if (pivots.S1) levels.push({ price: pivots.S1, source: 'S1', strength: 60 });
        if (pivots.S2) levels.push({ price: pivots.S2, source: 'S2', strength: 50 });

        // Add Fibonacci levels
        if (fibLevels?.levels && Array.isArray(fibLevels.levels)) {
            fibLevels.levels.forEach(fib => {
                if (fib.price) {
                    levels.push({ price: fib.price, source: `Fib ${fib.level}`, strength: 55 });
                }
            });
        }

        // Add EMA levels
        const ema60 = currentState?.trendStatus?.h4Ema60?.emaValue;
        const ema100 = currentState?.trendStatus?.h4Ema100?.emaValue;
        if (ema60) levels.push({ price: ema60, source: 'EMA60', strength: 45 });
        if (ema100) levels.push({ price: ema100, source: 'EMA100', strength: 50 });

        return levels;
    }, [currentPrice, fibLevels, signal?.pivot, currentState]);

    // PHASE 8.2: Structure events (placeholder - would come from API)
    const structureEvents = quantXData?.structureEvents || [];
    const structureScore = quantXData?.structureScore || 0;

    const handleToggle = () => {
        // Don't toggle if force expanded or in compact mode
        if (!forceExpanded) {
            setInternalExpanded(!internalExpanded);
        }
    };

    const handleAskQ = (e) => {
        e.stopPropagation();
        if (onAskQ) {
            onAskQ(signal);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════════════
    // PHASE 9: Section-Specific Q.AI Handlers
    // Generate context-aware prompts for each indicator section
    // ═══════════════════════════════════════════════════════════════════════════════
    const handleAskDNA = () => {
        if (onAskQ) {
            const prompt = `Analyze the Strategy DNA for ${symbol}: bounce rate ${strategyDNA?.bounceRate || '--'}%, break rate ${strategyDNA?.breakRate || '--'}%, fakeout rate ${strategyDNA?.fakeoutRate || '--'}%. What does this tell me about price behavior at key levels?`;
            onAskQ({ ...signal, customPrompt: prompt });
        }
    };

    const handleAskADX = () => {
        if (onAskQ) {
            const h4Adx = adxAnalysis?.h4?.adx || currentState?.volatility?.h4Adx || '--';
            const regime = adxAnalysis?.regime || 'UNKNOWN';
            const prompt = `Explain the ADX/ATR momentum for ${symbol}: H4 ADX is ${h4Adx}, market regime is ${regime}. Is this a good trending or ranging environment?`;
            onAskQ({ ...signal, customPrompt: prompt });
        }
    };

    const handleAskFib = () => {
        if (onAskQ) {
            const nearest = fibLevels?.nearestLevel || '--';
            const prompt = `Analyze the Fibonacci levels for ${symbol}. Price is near ${nearest}. What are the key support/resistance zones and potential bounce/break scenarios?`;
            onAskQ({ ...signal, customPrompt: prompt });
        }
    };

    const handleAskEMA = () => {
        if (onAskQ) {
            const score = currentState?.trendScore || '--';
            const trend = currentState?.trendDescription || currentState?.overallTrend || '--';
            const prompt = `Explain the EMA trend alignment for ${symbol}: Trend score is ${score}/100, direction is ${trend}. What does the multi-timeframe EMA structure suggest?`;
            onAskQ({ ...signal, customPrompt: prompt });
        }
    };

    const handleAskPivot = () => {
        if (onAskQ) {
            const pp = signal?.pivot?.pp || '--';
            const prompt = `Analyze the pivot points for ${symbol}. Pivot is at ${pp}. Which support (S1/S2/S3) or resistance (R1/R2/R3) levels are most significant today?`;
            onAskQ({ ...signal, customPrompt: prompt });
        }
    };

    // ═══════════════════════════════════════════════════════════════════════════════
    // COMPACT MODE - Minimal view for unselected cards in grid
    // ═══════════════════════════════════════════════════════════════════════════════
    if (isCompact) {
        return (
            <Box
                onClick={handleAskQ}
                sx={{
                    backgroundColor: COLORS.bgCard,
                    border: `1px solid ${COLORS.borderDefault}`,
                    borderRadius: RADIUS.sm,
                    padding: '8px 10px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                        borderColor: COLORS.borderHover,
                        backgroundColor: COLORS.bgHover,
                        transform: 'translateY(-1px)'
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {/* Left: Status Dot + Symbol */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: statusColor,
                                boxShadow: `0 0 4px ${statusColor}`
                            }}
                        />
                        <Typography
                            sx={{
                                fontSize: '11px',
                                fontWeight: 600,
                                color: COLORS.textPrimary,
                                fontFamily: "'JetBrains Mono', monospace"
                            }}
                        >
                            {symbol?.replace('_ct', '').replace('/', '')}
                        </Typography>
                    </Box>

                    {/* Right: Score Badge */}
                    <Box
                        sx={{
                            backgroundColor: `${statusColor}15`,
                            color: statusColor,
                            fontSize: '10px',
                            fontWeight: 700,
                            padding: '2px 6px',
                            borderRadius: RADIUS.xs,
                            fontFamily: "'JetBrains Mono', monospace"
                        }}
                    >
                        {verdict.score}%
                    </Box>
                </Box>
            </Box>
        );
    }

    // ═══════════════════════════════════════════════════════════════════════════════
    // FULL MODE - Normal or Expanded Card View
    // ═══════════════════════════════════════════════════════════════════════════════
    return (
        <Box
            sx={{
                backgroundColor: COLORS.bgCard,
                border: `1px solid ${COLORS.borderDefault}`,
                borderRadius: RADIUS.md,
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                '&:hover': {
                    borderColor: COLORS.borderHover,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                }
            }}
        >
            {/* Card Header - Always Visible */}
            <Box
                onClick={handleToggle}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 10px',
                    cursor: 'pointer',
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    borderBottom: isExpanded ? `1px solid ${COLORS.borderDefault}` : 'none',
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                        backgroundColor: COLORS.bgHover
                    }
                }}
            >
                {/* Left: Status Dot + Symbol */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
                    {/* Status Indicator Dot */}
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: statusColor,
                            boxShadow: `0 0 6px ${statusColor}`
                        }}
                    />

                    {/* Symbol */}
                    <Typography
                        sx={{
                            fontSize: '13px',
                            fontWeight: 700,
                            color: COLORS.textPrimary,
                            fontFamily: "'Inter', -apple-system, sans-serif",
                            letterSpacing: '0.02em'
                        }}
                    >
                        {symbol?.replace('_ct', '') || symbol}
                    </Typography>

                    {/* Signal Badge */}
                    <Box
                        sx={{
                            backgroundColor: getSignalBg(verdict.type),
                            color: statusColor,
                            fontSize: '10px',
                            fontWeight: 700,
                            padding: '2px 8px',
                            borderRadius: RADIUS.xs,
                            border: `1px solid ${getSignalBorder(verdict.type)}`,
                            letterSpacing: '0.03em'
                        }}
                    >
                        {verdict.action}
                    </Box>

                    {/* ATR Badge (Phase 1E) */}
                    {currentState?.volatility?.h4Atr && (
                        <Box
                            sx={{
                                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                color: '#3b82f6',
                                fontSize: '10px',
                                fontWeight: 600,
                                padding: '2px 6px',
                                borderRadius: RADIUS.xs,
                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                fontFamily: "'JetBrains Mono', monospace",
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            <span style={{ color: '#9ca3af' }}>ATR:</span>
                            {currentState.volatility.h4Atr.toFixed(4)}
                            <span style={{ color: '#6b7280' }}>
                                ({currentPrice > 0 ? ((currentState.volatility.h4Atr / currentPrice) * 100).toFixed(2) : '0.00'}%)
                            </span>
                        </Box>
                    )}
                </Box>

                {/* Right: Score + Expand */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
                    {/* Score */}
                    <Box
                        sx={{
                            backgroundColor: `${statusColor}20`,
                            color: statusColor,
                            fontSize: '11px',
                            fontWeight: 700,
                            padding: '3px 8px',
                            borderRadius: RADIUS.xs,
                            fontFamily: "'JetBrains Mono', monospace"
                        }}
                    >
                        {verdict.score}%
                    </Box>

                    {/* Expand Arrow */}
                    <IconButton
                        size="small"
                        sx={{
                            color: COLORS.textMuted,
                            padding: '2px',
                            '&:hover': { color: COLORS.textSecondary }
                        }}
                    >
                        {isExpanded ? (
                            <ExpandLessIcon sx={{ fontSize: '18px' }} />
                        ) : (
                            <ExpandMoreIcon sx={{ fontSize: '18px' }} />
                        )}
                    </IconButton>
                </Box>
            </Box>

            {/* Expanded Content - AUDIT: Tighter padding and gap */}
            <Collapse in={isExpanded}>
                <Box
                    sx={{
                        padding: '6px',
                        backgroundColor: 'rgba(0, 0, 0, 0.15)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px'
                    }}
                >
                    {/* ═══════════════════════════════════════════════════════════════════
                        PHASE 2.2: USER-FRIENDLY QUICK SUMMARY (FULL WIDTH - TOP)
                        Clear, bold outcome for non-technical users
                        ═══════════════════════════════════════════════════════════════════ */}
                    <QuickSummaryPanel
                        verdict={verdict}
                        dnaData={strategyDNA}
                        trendScore={currentState?.trendScore}
                        session={currentState?.session}
                    />

                    {/* ═══════════════════════════════════════════════════════════════════
                        PHASE 7.3: TRADE SETUP ROW (FULL WIDTH)
                        Shows Entry, SL, TP1, TP2, R:R with action buttons
                        Positioned prominently after QuickSummary, before grid
                        ═══════════════════════════════════════════════════════════════════ */}
                    {tradeLevels && tradeDirection && (
                        <TradeSetupRow
                            direction={tradeDirection}
                            entry={tradeLevels.entry}
                            stopLoss={tradeLevels.stopLoss}
                            takeProfit1={tradeLevels.takeProfit1}
                            takeProfit2={tradeLevels.takeProfit2}
                            confidence={verdict.score}
                            reasons={tradeLevels.reasons}
                            onAskQ={() => onAskQ && onAskQ(signal)}
                        />
                    )}

                    {/* ═══════════════════════════════════════════════════════════════════
                        PHASE 2.2: 2-COLUMN GRID LAYOUT
                        Left: Strategy DNA, EMA Trend, Momentum
                        Right: Fibonacci, Trading Signal, Key Levels
                        Gap: 8px (SPACING.sm)
                        ═══════════════════════════════════════════════════════════════════ */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '6px'  // AUDIT: Reduced from 8px to 6px for density
                        }}
                    >
                        {/* ═══════════════════════════════════════════════════════════════
                            COLUMN 1: Strategy DNA + EMA + Momentum
                            AUDIT: Reduced gap from 6px to 4px for density
                            ═══════════════════════════════════════════════════════════════ */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {/* Strategy DNA - Primary indicator + PHASE 9: Ask Q */}
                            {strategyDNA && (
                                <EnhancedDNASection
                                    strategyDNA={strategyDNA}
                                    strategyDNAStats={strategyDNAStats}
                                    onAskQ={handleAskDNA}
                                />
                            )}

                            {/* TASK 1.3: Fakeout Warnings Panel - High-risk levels (>30% fakeout) */}
                            {strategyDNAStats?.length > 0 && (
                                <FakeoutWarningsPanel
                                    warnings={strategyDNAStats.filter(stat => (stat.fakeoutRate || 0) > 30)}
                                    defaultExpanded={false}
                                />
                            )}

                            {/* EMA Trend Matrix - AUDIT: Removed duplicate header + PHASE 9: Ask Q */}
                            {(currentState || trendFilterStats) && (
                                <EMATrendMatrix
                                    currentState={currentState}
                                    trendFilterStats={trendFilterStats}
                                    onAskQ={handleAskEMA}
                                />
                            )}

                            {/* ADX/ATR Momentum Panel - AUDIT: Removed duplicate header + PHASE 9: Ask Q */}
                            {(adxAnalysis || currentState?.volatility) && (
                                <ADXATRPivotTable
                                    adxAnalysis={adxAnalysis}
                                    currentState={currentState}
                                    onAskQ={handleAskADX}
                                />
                            )}

                            {/* PHASE 6.3: Pivot Points Table - Always render + PHASE 9: Ask Q */}
                            <PivotPointsTable
                                pivots={signal?.pivot || {}}
                                currentPrice={currentPrice}
                                fibLevels={fibLevels}
                                emaLevels={{
                                    ema60: currentState?.trendStatus?.h4Ema60?.emaValue,
                                    ema100: currentState?.trendStatus?.h4Ema100?.emaValue,
                                }}
                                timeframe="D1"
                                onAskQ={handleAskPivot}
                            />
                        </Box>

                        {/* ═══════════════════════════════════════════════════════════════
                            COLUMN 2: Fibonacci + Signal + Key Levels
                            AUDIT: Reduced gap from 6px to 4px for density
                            ═══════════════════════════════════════════════════════════════ */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {/* TASK 2: Multi-TF Fibonacci Table - Replaced FibonacciCompactTable + PHASE 9: Ask Q */}
                            {(fibLevels || quantXData?.strategyDashboard?.fibonacciLevels) && (
                                <MultiFibTable
                                    fibData={fibLevels || quantXData?.strategyDashboard?.fibonacciLevels}
                                    currentPrice={currentPrice}
                                    pivots={signal?.pivot || {}}
                                    emaLevels={{
                                        ema60: currentState?.trendStatus?.h4Ema60?.emaValue,
                                        ema100: currentState?.trendStatus?.h4Ema100?.emaValue,
                                    }}
                                    onAskQ={handleAskFib}
                                />
                            )}

                            {/* Trading Signal + Key Levels - AUDIT: Combined into single compact panel */}
                            <Box
                                sx={{
                                    backgroundColor: 'rgba(55, 65, 81, 0.15)',
                                    borderRadius: '4px',
                                    border: '1px solid rgba(55, 65, 81, 0.25)',
                                    overflow: 'hidden'
                                }}
                            >
                                {/* Signal Header - AUDIT: Compact 24px height */}
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    height: '24px',
                                    padding: '0 8px',
                                    backgroundColor: 'rgba(55, 65, 81, 0.2)',
                                    borderBottom: '1px solid rgba(55, 65, 81, 0.25)'
                                }}>
                                    <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        SIGNAL
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography sx={{ fontSize: '10px', fontWeight: 700, color: statusColor, fontFamily: "'JetBrains Mono', monospace" }}>
                                            {verdict.action}
                                        </Typography>
                                        <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#fff', fontFamily: "'JetBrains Mono', monospace" }}>
                                            {verdict.score}%
                                        </Typography>
                                        <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#fbbf24', fontFamily: "'JetBrains Mono', monospace" }}>
                                            {currentState?.session || '--'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Key Levels Grid - AUDIT: 3-column compact grid */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1px', backgroundColor: 'rgba(55, 65, 81, 0.2)' }}>
                                    {/* Current Price */}
                                    <Box sx={{ backgroundColor: '#0a0a0a', padding: '4px 6px', textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: '8px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em' }}>CURRENT</Typography>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#00d4ff', fontFamily: "'JetBrains Mono', monospace" }}>
                                            {currentPrice > 0 ? currentPrice.toFixed(currentPrice >= 100 ? 2 : 5) : '--'}
                                        </Typography>
                                    </Box>
                                    {/* Swing High */}
                                    <Box sx={{ backgroundColor: '#0a0a0a', padding: '4px 6px', textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: '8px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em' }}>HIGH</Typography>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#ef4444', fontFamily: "'JetBrains Mono', monospace" }}>
                                            {fibLevels?.swingHigh ? fibLevels.swingHigh.toFixed(fibLevels.swingHigh >= 100 ? 2 : 5) : '--'}
                                        </Typography>
                                    </Box>
                                    {/* Swing Low */}
                                    <Box sx={{ backgroundColor: '#0a0a0a', padding: '4px 6px', textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: '8px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em' }}>LOW</Typography>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#22c55e', fontFamily: "'JetBrains Mono', monospace" }}>
                                            {fibLevels?.swingLow ? fibLevels.swingLow.toFixed(fibLevels.swingLow >= 100 ? 2 : 5) : '--'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* PHASE 8.1: Key Levels Table - Shows all significant levels */}
                            {keyLevels.length > 0 && (
                                <KeyLevelsTable
                                    levels={keyLevels}
                                    currentPrice={currentPrice}
                                />
                            )}

                            {/* PHASE 8.2: Market Structure Panel - BOS/CHoCH events */}
                            <StructurePanel
                                events={structureEvents}
                                score={structureScore}
                            />
                        </Box>
                    </Box>

                    {/* AUDIT: Combined Ask Q + Position Summary into single compact footer */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '4px 8px',
                            backgroundColor: '#1a1a1a',
                            borderTop: '1px solid #374151',
                            borderRadius: '0 0 4px 4px',
                            marginTop: '2px'
                        }}
                    >
                        {/* Left: Position Status */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                <Box sx={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#6b7280' }} />
                                <Typography sx={{ fontSize: '9px', color: '#6b7280', fontFamily: "'JetBrains Mono', monospace" }}>OPEN</Typography>
                                <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#fff', fontFamily: "'JetBrains Mono', monospace" }}>0</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                <TrendingUpIcon sx={{ fontSize: 10, color: '#22c55e' }} />
                                <Typography sx={{ fontSize: '9px', color: '#22c55e', fontFamily: "'JetBrains Mono', monospace" }}>0.00</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                <TrendingDownIcon sx={{ fontSize: 10, color: '#ef4444' }} />
                                <Typography sx={{ fontSize: '9px', color: '#ef4444', fontFamily: "'JetBrains Mono', monospace" }}>0.00</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#9ca3af', fontFamily: "'JetBrains Mono', monospace" }}>P&L: $0</Typography>
                        </Box>

                        {/* Right: Ask Q Button - AUDIT: Compact inline button */}
                        {onAskQ && (
                            <Tooltip title={`Ask Q about ${symbol}`} arrow>
                                <Box
                                    onClick={handleAskQ}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        padding: '3px 8px',
                                        backgroundColor: 'rgba(39, 181, 109, 0.1)',
                                        border: '1px solid rgba(39, 181, 109, 0.25)',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        transition: 'all 0.12s ease',
                                        '&:hover': {
                                            backgroundColor: 'rgba(39, 181, 109, 0.2)',
                                            borderColor: 'rgba(39, 181, 109, 0.4)'
                                        }
                                    }}
                                >
                                    <ShowChartIcon sx={{ fontSize: '11px', color: COLORS.signalBuy }} />
                                    <Typography sx={{ fontSize: '9px', fontWeight: 600, color: COLORS.signalBuy }}>
                                        Ask Q
                                    </Typography>
                                </Box>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
});

QSignalGridCard.displayName = 'QSignalGridCard';

QSignalGridCard.propTypes = {
    signal: PropTypes.shape({
        symbol: PropTypes.string.isRequired,
        qSignal: PropTypes.string,
        qScore: PropTypes.number,
        zSignal: PropTypes.string,
        zScore: PropTypes.number,
        trendScore: PropTypes.number,
        pivot: PropTypes.object,
        distribution: PropTypes.object,
        fibLevels: PropTypes.object,
        ema: PropTypes.object
    }).isRequired,
    onAskQ: PropTypes.func,
    defaultExpanded: PropTypes.bool,
    // Phase 1B: Enhanced Signal Grid
    quantXData: PropTypes.object,
    isSelected: PropTypes.bool,
    // Phase 1D: Expanded Selected Card Layout
    isCompact: PropTypes.bool,
    isExpanded: PropTypes.bool
};

LevelRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.number,
    color: PropTypes.string,
    bgColor: PropTypes.string
};

export default QSignalGridCard;
